'use client';

import Link from 'next/link';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { ChevronDown, ChevronRight, CircleHelp, ExternalLink, ListPlus, Share2, SlidersHorizontal } from 'lucide-react';
import { CompareWithDialog } from '@/components/comparator/CompareWithDialog';
import { useInstrumentCache } from '@/components/catalogo/ticker-cache-provider';
import type { InstrumentRow } from '@/components/catalogo/types';
import { useAppColors } from '@/styles/colors';
import type { AppColors } from '@/styles/colors/types';
import {
  assertComparatorIndicatorIdsCoverage,
  decodeComparatorIndicatorsBitmap,
  encodeComparatorIndicatorsBitmap,
  INDICATORS_BITMAP_PREFIX,
} from '@/libs/comparator-indicator-ids';
import { getDefaultCollapsedSections } from '@/libs/comparator-default-sections';
import { getComparatorHeading, shouldRenderComparatorHeading } from './comparador-heading';
import { buildComparatorPath } from '@/libs/comparador-route';
import { isTreasuryInstrument } from '@/libs/normalize-ticker';

const ASSETS_QUERY_KEY = 'ativos';
const INDICATORS_QUERY_KEY = 'indicadores';
const MAX_ASSETS = 5;
const NONE_INDICATORS_TOKEN = 'none';
const EMPTY_ASSETS: string[] = [];
const EMPTY_INDICATORS: string[] = [];

const PRIMARY_COMPARATOR_KEYS = new Set<string>(['preco_atual', 'pvp', 'dy12m', 'numero_cotistas', 'liquidez_30d']);

const FUNDAMENTALS_DISPLAY_ORDER = [
  'preco_atual',
  'pvp',
  'dy12m',
  'numero_cotistas',
  'ultimo_dividendo',
  'vp_por_cota',
  'patrimonio',
  'valor_mercado',
  'dividendos12m',
  'numero_cotas',
] as const;

const MARKET_DISPLAY_ORDER = ['liquidez_30d', 'dy_mes', 'pvp_discount_hist'] as const;

type SectionKey = 'basicos' | 'fundamentos' | 'mercado' | 'portfolio' | 'quantitativos' | 'cotacao' | 'risco';

type IndicatorDefinition = {
  key: string;
  label: string;
  description: string;
  section: SectionKey;
  value: (ctx: {
    row: InstrumentRow | null;
    risk: RiskMetrics | null;
    fundamentals: FundamentalsSnapshot | null;
  }) => number | null;
  format: (value: number | null) => string;
};

type PricePoint = {
  date: string;
  close: number;
};

type DividendPoint = {
  date: string;
  amount: number;
};

type QuoteRangeId = '3M' | '6M' | '1A' | '2A' | '3A' | '5A' | 'TUDO';

type RiskMetrics = {
  periodReturnPct: number | null;
  maxDrawdownPct: number | null;
  annualVolatilityPct: number | null;
};

type FundamentalsSnapshot = {
  idCem: string | null;
  currentPrice: number | null;
  latestDividendAmount: number | null;
  currentVpPerShare: number | null;
  patrimonioLiquido: number | null;
  currentPvp: number | null;
  valorMercado: number | null;
  dy12mPct: number | null;
  dividends12mAmount: number | null;
  numeroCotistas: number | null;
  cotasEmitidas: number | null;
  cashResourcesValue: number | null;
  receivablesValue: number | null;
  payablesValue: number | null;
  netLiquidityValue: number | null;
  cashOverInvestedPct: number | null;
  portfolioFinancialAssetsPct: number | null;
  portfolioFundsPct: number | null;
  realEstateTotalAssets: number | null;
  realEstateVacancyAvgPct: number | null;
  realEstateDelinquencyAvgPct: number | null;
  realEstateTypicalContractsPct: number | null;
  realEstateAtypicalContractsPct: number | null;
  paperTop5ConcentrationPct: number | null;
  paperInflationExposurePct: number | null;
  paperMaturityUpto12mPct: number | null;
  paperDominantIndexerPct: number | null;
};

type PortfolioTopPosition = {
  name: string;
  portfolioPercent: number | null;
};

type PortfolioHighlightsSnapshot = {
  idCem: string | null;
  referenceDate: string | null;
  topPositions: PortfolioTopPosition[];
  mix: {
    realEstatePercent: number;
    fundsPercent: number;
    financialAssetsPercent: number;
    equitiesPercent: number;
  } | null;
  concentrationByProfile: {
    realEstateTop1Percent: number | null;
    paperTop1Percent: number | null;
  } | null;
};

const PERCENT_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const NUMBER_FORMATTER = new Intl.NumberFormat('pt-BR', {
  maximumFractionDigits: 2,
});
const INTEGER_FORMATTER = new Intl.NumberFormat('pt-BR', {
  maximumFractionDigits: 0,
});
const BRL_FORMATTER = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const COMPACT_NUMBER_FORMATTER = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});
const DAY_MS = 24 * 60 * 60 * 1000;
const QUOTE_RANGE_OPTIONS: Array<{
  id: QuoteRangeId;
  label: string;
  days: number | null;
}> = [
  { id: '3M', label: '3M', days: 90 },
  { id: '6M', label: '6M', days: 180 },
  { id: '1A', label: '1A', days: 365 },
  { id: '2A', label: '2A', days: 365 * 2 },
  { id: '3A', label: '3A', days: 365 * 3 },
  { id: '5A', label: '5A', days: 365 * 5 },
  { id: 'TUDO', label: 'TUDO', days: null },
];

const SECTION_LABELS: Record<SectionKey, string> = {
  basicos: 'Dados básicos',
  fundamentos: 'Fundamentos',
  mercado: 'Métricas de Mercado',
  portfolio: 'Portfólio',
  quantitativos: 'Indicadores Quantitativos',
  cotacao: 'Cotação',
  risco: 'Risco e Momento',
};

const COTACAO_CHART_ROWS: Array<{
  key: string;
  label: string;
  description: string;
}> = [
  {
    key: 'grafico_cotacao_normalizada',
    label: 'Cotação normalizada (base 100)',
    description: 'Compara a trajetória de preço partindo da mesma base (100) no início do período.',
  },
  {
    key: 'grafico_cotacao_dividendos',
    label: 'Cotação + dividendos acumulados (base 100)',
    description: 'Soma a cotação com os dividendos pagos desde o início, tudo em base 100.',
  },
  {
    key: 'grafico_dividendos_periodo',
    label: 'Dividendos por período (valor por cota)',
    description: 'Barras por período com valor de dividendos por cota para cada ativo.',
  },
];

const PORTFOLIO_VISUAL_ROWS: Array<{
  key: string;
  label: string;
  description: string;
}> = [
  {
    key: 'portfolio_top5',
    label: 'Top 5 posições (% PL)',
    description: 'Mostra as cinco maiores posições como percentual do patrimônio.',
  },
  {
    key: 'portfolio_mix',
    label: 'Mix do portfolio',
    description: 'Distribuição do patrimônio entre imóveis, fundos, ativos financeiros e participações.',
  },
  {
    key: 'portfolio_concentracao',
    label: 'Concentração (Tijolo e Papel)',
    description: 'Mostra concentração por perfil, com leitura qualitativa.',
  },
];

const BASIC_DATA_ROWS: Array<{
  key: string;
  label: string;
  description: string;
  value: (row: InstrumentRow | null) => string | null;
}> = [
  {
    key: 'descricao',
    label: 'DESCRIÇÃO',
    description: 'Nome/descrição principal do ativo no cadastro.',
    value: (row) => row?.name?.trim() || null,
  },
  {
    key: 'administradora',
    label: 'ADMINISTRADORA/GESTÃO',
    description: 'Instituição responsável pela administração ou gestão do fundo.',
    value: (row) => row?.gestao?.trim() || row?.companyName?.trim() || null,
  },
  {
    key: 'classificacao',
    label: 'CLASSIFICAÇÃO',
    description: 'Classe do fundo conforme metadados disponíveis.',
    value: (row) => row?.classificacaoLabel?.trim() || row?.classificacao?.trim() || null,
  },
  {
    key: 'segmento',
    label: 'SEGMENTO',
    description: 'Segmento de atuação predominante do fundo.',
    value: (row) => row?.segmento?.trim() || null,
  },
  {
    key: 'tipo_fundo',
    label: 'TIPO DE FUNDO',
    description: 'Tipo/categoria do fundo no catálogo.',
    value: (row) => row?.fundTypeDescription?.trim() || row?.fundTypeSymbol?.trim() || null,
  },
];

const formatPercent = (value: number | null) => (value === null ? '-' : `${PERCENT_FORMATTER.format(value)}%`);
const formatInteger = (value: number | null) => (value === null ? '-' : INTEGER_FORMATTER.format(value));
const formatRatio = (value: number | null) =>
  value === null
    ? '-'
    : value.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
const formatCurrency = (value: number | null) => {
  if (value === null) return '-';
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `R$ ${COMPACT_NUMBER_FORMATTER.format(value / 1_000_000_000)} bi`;
  if (abs >= 1_000_000) return `R$ ${COMPACT_NUMBER_FORMATTER.format(value / 1_000_000)} mi`;
  return BRL_FORMATTER.format(value);
};

const INDICATORS: IndicatorDefinition[] = [
  {
    key: 'preco_atual',
    label: 'PREÇO ATUAL',
    description: 'Preço mais recente da cota.',
    section: 'fundamentos',
    value: ({ fundamentals }) => fundamentals?.currentPrice ?? null,
    format: formatCurrency,
  },
  {
    key: 'ultimo_dividendo',
    label: 'ÚLTIMO DIVIDENDO',
    description: 'Último dividendo por cota registrado.',
    section: 'fundamentos',
    value: ({ fundamentals }) => fundamentals?.latestDividendAmount ?? null,
    format: formatCurrency,
  },
  {
    key: 'vp_por_cota',
    label: 'VAL. PATRIMONIAL P/COTA',
    description: 'Valor patrimonial por cota.',
    section: 'fundamentos',
    value: ({ fundamentals }) => fundamentals?.currentVpPerShare ?? null,
    format: formatCurrency,
  },
  {
    key: 'patrimonio',
    label: 'PATRIMÔNIO',
    description: 'Patrimônio líquido total do fundo.',
    section: 'fundamentos',
    value: ({ fundamentals }) => fundamentals?.patrimonioLiquido ?? null,
    format: formatCurrency,
  },
  {
    key: 'pvp',
    label: 'P/VP',
    description: 'Relação entre preço da cota e valor patrimonial por cota.',
    section: 'fundamentos',
    value: ({ fundamentals }) => fundamentals?.currentPvp ?? null,
    format: formatRatio,
  },
  {
    key: 'valor_mercado',
    label: 'VALOR DE MERCADO',
    description: 'Estimativa do valor de mercado do fundo.',
    section: 'fundamentos',
    value: ({ fundamentals }) => fundamentals?.valorMercado ?? null,
    format: formatCurrency,
  },
  {
    key: 'dy12m',
    label: 'DY (12M)',
    description: 'Dividend yield acumulado em 12 meses.',
    section: 'fundamentos',
    value: ({ fundamentals }) => fundamentals?.dy12mPct ?? null,
    format: formatPercent,
  },
  {
    key: 'dividendos12m',
    label: 'DIVIDENDOS (12M)',
    description: 'Soma de dividendos por cota nos últimos 12 meses.',
    section: 'fundamentos',
    value: ({ fundamentals }) => fundamentals?.dividends12mAmount ?? null,
    format: formatCurrency,
  },
  {
    key: 'numero_cotistas',
    label: 'Nº DE COTISTAS',
    description: 'Quantidade de cotistas do fundo.',
    section: 'fundamentos',
    value: ({ fundamentals }) => fundamentals?.numeroCotistas ?? null,
    format: formatInteger,
  },
  {
    key: 'numero_cotas',
    label: 'Nº DE COTAS',
    description: 'Quantidade total de cotas emitidas.',
    section: 'fundamentos',
    value: ({ fundamentals }) => fundamentals?.cotasEmitidas ?? null,
    format: formatInteger,
  },
  {
    key: 'liquidez_30d',
    label: 'LIQUIDEZ DIÁRIA (30D)',
    description: 'Média de liquidez diária de negociação nos últimos 30 dias.',
    section: 'mercado',
    value: ({ row }) => row?.liquidezDiaria30d ?? null,
    format: formatCurrency,
  },
  {
    key: 'dy_mes',
    label: 'DY (MÊS)',
    description: 'Dividend yield do mês.',
    section: 'mercado',
    value: ({ row }) => row?.dyMesPct ?? null,
    format: formatPercent,
  },
  {
    key: 'pvp_discount_hist',
    label: 'DESCONTO P/VP VS HISTÓRICO',
    description: 'Desvio do P/VP atual versus sua média histórica.',
    section: 'mercado',
    value: ({ row }) => row?.pvpDiscountToHistAvgPct ?? null,
    format: formatPercent,
  },
  {
    key: 'cash_resources_value',
    label: 'CAIXA (RECURSOS)',
    description: 'Total de recursos em caixa no snapshot do portfólio.',
    section: 'portfolio',
    value: ({ fundamentals }) => fundamentals?.cashResourcesValue ?? null,
    format: formatCurrency,
  },
  {
    key: 'receivables_value',
    label: 'A RECEBER',
    description: 'Valor total de direitos/recebíveis no snapshot do portfólio.',
    section: 'portfolio',
    value: ({ fundamentals }) => fundamentals?.receivablesValue ?? null,
    format: formatCurrency,
  },
  {
    key: 'payables_value',
    label: 'A PAGAR',
    description: 'Valor total de obrigações a pagar no snapshot do portfólio.',
    section: 'portfolio',
    value: ({ fundamentals }) => fundamentals?.payablesValue ?? null,
    format: formatCurrency,
  },
  {
    key: 'net_liquidity_value',
    label: 'FOLGA LÍQUIDA',
    description: 'Caixa líquido após considerar recebíveis e obrigações.',
    section: 'portfolio',
    value: ({ fundamentals }) => fundamentals?.netLiquidityValue ?? null,
    format: formatCurrency,
  },
  {
    key: 'cash_over_invested_pct',
    label: 'CAIXA / INVESTIDO',
    description: 'Percentual de caixa em relação ao total investido.',
    section: 'portfolio',
    value: ({ fundamentals }) => fundamentals?.cashOverInvestedPct ?? null,
    format: formatPercent,
  },
  {
    key: 'portfolio_financial_assets_pct',
    label: 'ATIVOS FINANCEIROS NO PORTFÓLIO',
    description: 'Parcela de ativos financeiros no mix do portfólio.',
    section: 'portfolio',
    value: ({ fundamentals }) => fundamentals?.portfolioFinancialAssetsPct ?? null,
    format: formatPercent,
  },
  {
    key: 'portfolio_funds_pct',
    label: 'FUNDOS NO PORTFÓLIO',
    description: 'Parcela de fundos no mix do portfólio.',
    section: 'portfolio',
    value: ({ fundamentals }) => fundamentals?.portfolioFundsPct ?? null,
    format: formatPercent,
  },
  {
    key: 'real_estate_total_assets',
    label: 'ATIVOS IMOBILIÁRIOS (QTD.)',
    description: 'Quantidade total de ativos imobiliários considerados no snapshot.',
    section: 'portfolio',
    value: ({ fundamentals }) => fundamentals?.realEstateTotalAssets ?? null,
    format: formatInteger,
  },
  {
    key: 'real_estate_vacancy_avg_pct',
    label: 'VACÂNCIA MÉDIA (TIJOLO)',
    description: 'Vacância média dos ativos imobiliários do portfólio.',
    section: 'portfolio',
    value: ({ fundamentals }) => fundamentals?.realEstateVacancyAvgPct ?? null,
    format: formatPercent,
  },
  {
    key: 'real_estate_delinquency_avg_pct',
    label: 'INADIMPLÊNCIA MÉDIA (TIJOLO)',
    description: 'Inadimplência média dos ativos imobiliários do portfólio.',
    section: 'portfolio',
    value: ({ fundamentals }) => fundamentals?.realEstateDelinquencyAvgPct ?? null,
    format: formatPercent,
  },
  {
    key: 'real_estate_typical_contracts_pct',
    label: 'CONTRATOS TÍPICOS (TIJOLO)',
    description: 'Participação de contratos típicos entre os contratos imobiliários.',
    section: 'portfolio',
    value: ({ fundamentals }) => fundamentals?.realEstateTypicalContractsPct ?? null,
    format: formatPercent,
  },
  {
    key: 'real_estate_atypical_contracts_pct',
    label: 'CONTRATOS ATÍPICOS (TIJOLO)',
    description: 'Participação de contratos atípicos entre os contratos imobiliários.',
    section: 'portfolio',
    value: ({ fundamentals }) => fundamentals?.realEstateAtypicalContractsPct ?? null,
    format: formatPercent,
  },
  {
    key: 'paper_top5_concentration_pct',
    label: 'TOP 5 (PAPEL)',
    description: 'Concentração do top 5 no portfólio de papel.',
    section: 'portfolio',
    value: ({ fundamentals }) => fundamentals?.paperTop5ConcentrationPct ?? null,
    format: formatPercent,
  },
  {
    key: 'paper_inflation_exposure_pct',
    label: 'EXPOSIÇÃO À INFLAÇÃO (PAPEL)',
    description: 'Parcela exposta a indexadores de inflação no portfólio de papel.',
    section: 'portfolio',
    value: ({ fundamentals }) => fundamentals?.paperInflationExposurePct ?? null,
    format: formatPercent,
  },
  {
    key: 'paper_maturity_upto_12m_pct',
    label: 'VENCIMENTO ATÉ 12M (PAPEL)',
    description: 'Parcela com vencimento em até 12 meses no portfólio de papel.',
    section: 'portfolio',
    value: ({ fundamentals }) => fundamentals?.paperMaturityUpto12mPct ?? null,
    format: formatPercent,
  },
  {
    key: 'paper_dominant_indexer_pct',
    label: 'INDEXADOR DOMINANTE (PAPEL)',
    description: 'Participação do indexador dominante no portfólio de papel.',
    section: 'portfolio',
    value: ({ fundamentals }) => fundamentals?.paperDominantIndexerPct ?? null,
    format: formatPercent,
  },
  {
    key: 'vacancia',
    label: 'Vacância',
    description: 'Percentual médio de vacância reportada.',
    section: 'quantitativos',
    value: ({ row }) => row?.vacanciaPct ?? null,
    format: formatPercent,
  },
  {
    key: 'inadimplencia',
    label: 'Inadimplência',
    description: 'Percentual médio de inadimplência reportada.',
    section: 'quantitativos',
    value: ({ row }) => row?.inadimplenciaPct ?? null,
    format: formatPercent,
  },
  {
    key: 'selic_sensitivity',
    label: 'Sensibilidade à Selic',
    description: 'Estimativa de sensibilidade do resultado a variações da Selic.',
    section: 'quantitativos',
    value: ({ row }) => row?.selicSensitivityScore ?? null,
    format: formatPercent,
  },
  {
    key: 'leverage_index',
    label: 'Índice de alavancagem',
    description: 'Indicador de pressão financeira/alavancagem.',
    section: 'quantitativos',
    value: ({ row }) => row?.leverageIndexScore ?? null,
    format: formatPercent,
  },
  {
    key: 'vol_21d',
    label: 'Volatilidade anualizada (21d)',
    description: 'Volatilidade anualizada estimada com janela de 21 pregões.',
    section: 'quantitativos',
    value: ({ row }) => row?.volatilityAnnualized21dPct ?? null,
    format: formatPercent,
  },
  {
    key: 'distance_12m_high',
    label: 'Distância da máxima (12m)',
    description: 'Distância percentual em relação à máxima dos últimos 12 meses.',
    section: 'quantitativos',
    value: ({ row }) => row?.distanceTo12mHighPct ?? null,
    format: formatPercent,
  },
  {
    key: 'conc_tijolo',
    label: 'Concentração Tijolo',
    description: 'Concentração de exposição no perfil Tijolo.',
    section: 'quantitativos',
    value: ({ row }) => row?.concentracaoTijoloPct ?? null,
    format: formatPercent,
  },
  {
    key: 'conc_papel',
    label: 'Concentração Papel',
    description: 'Concentração de exposição no perfil Papel.',
    section: 'quantitativos',
    value: ({ row }) => row?.concentracaoPapelPct ?? null,
    format: formatPercent,
  },
  {
    key: 'retorno_periodo',
    label: 'Retorno no período (cotação)',
    description: 'Variação percentual da cotação no período selecionado.',
    section: 'risco',
    value: ({ risk }) => risk?.periodReturnPct ?? null,
    format: formatPercent,
  },
  {
    key: 'max_drawdown',
    label: 'Max drawdown (cotação)',
    description: 'Maior queda percentual acumulada da cotação no período.',
    section: 'risco',
    value: ({ risk }) => risk?.maxDrawdownPct ?? null,
    format: formatPercent,
  },
  {
    key: 'vol_realizada',
    label: 'Volatilidade anualizada realizada',
    description: 'Volatilidade anualizada calculada sobre retornos diários do período.',
    section: 'risco',
    value: ({ risk }) => risk?.annualVolatilityPct ?? null,
    format: formatPercent,
  },
];

const BASIC_DATA_KEYS = BASIC_DATA_ROWS.map((row) => row.key);
const COTACAO_CHART_KEYS = COTACAO_CHART_ROWS.map((item) => item.key);
const PORTFOLIO_VISUAL_KEYS = PORTFOLIO_VISUAL_ROWS.map((item) => item.key);
const ALL_INDICATOR_KEYS = INDICATORS.map((indicator) => indicator.key);
const ALL_SELECTABLE_KEYS = [
  ...BASIC_DATA_KEYS,
  ...ALL_INDICATOR_KEYS,
  ...COTACAO_CHART_KEYS,
  ...PORTFOLIO_VISUAL_KEYS,
];
assertComparatorIndicatorIdsCoverage(ALL_SELECTABLE_KEYS);
const SECTION_SELECTABLE_KEYS: Record<SectionKey, string[]> = {
  basicos: BASIC_DATA_KEYS,
  fundamentos: INDICATORS.filter((item) => item.section === 'fundamentos').map((item) => item.key),
  mercado: INDICATORS.filter((item) => item.section === 'mercado').map((item) => item.key),
  portfolio: [
    ...INDICATORS.filter((item) => item.section === 'portfolio').map((item) => item.key),
    ...PORTFOLIO_VISUAL_KEYS,
  ],
  quantitativos: INDICATORS.filter((item) => item.section === 'quantitativos').map((item) => item.key),
  cotacao: COTACAO_CHART_KEYS,
  risco: INDICATORS.filter((item) => item.section === 'risco').map((item) => item.key),
};

const normalizeInstrument = (value: string) =>
  value
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Z0-9]/g, '');

const toBaseInstrument = (value: string): string => {
  const normalized = normalizeInstrument(value);
  if (normalized.endsWith('11') && normalized.length >= 6) return normalized.slice(0, -2);
  return normalized;
};

const toInstrumentLabel = (value: string): string => {
  const normalized = normalizeInstrument(value);
  if (/^[A-Z]{4}$/.test(normalized)) return `${normalized}11`;
  return normalized;
};

const parseAssets = (value: string | null): string[] => {
  if (!value) return [];
  const parsed = value
    .replace(/-vs-/gi, '.')
    .split(/[.,]/)
    .filter((item) => !isTreasuryInstrument(item))
    .map((item) => toInstrumentLabel(item))
    .filter((item) => item.length >= 5);
  return Array.from(new Set(parsed)).slice(0, MAX_ASSETS);
};

const parseAssetsFromPathname = (pathname: string | null): string[] => {
  if (!pathname) return [];
  const match = pathname.match(/^\/comparador\/([^/?#]+)$/i);
  if (!match) return [];
  return parseAssets(match[1]);
};

const parseIndicators = (value: string | null): string[] => {
  if (!value) return [];
  if (value.trim().toLowerCase() === NONE_INDICATORS_TOKEN) return [];
  if (value.startsWith(INDICATORS_BITMAP_PREFIX)) {
    return decodeComparatorIndicatorsBitmap(value.slice(INDICATORS_BITMAP_PREFIX.length));
  }
  const parsed = value
    .split(',')
    .map((item) => item.trim().toLowerCase())
    .filter((item) => ALL_SELECTABLE_KEYS.includes(item));
  return Array.from(new Set(parsed));
};

const buildComparadorPathFromAssets = (items: string[]): string => {
  return buildComparatorPath(items);
};

function buildMonthlyTicks(minTs: number, maxTs: number, maxTicks = 8): number[] {
  if (!Number.isFinite(minTs) || !Number.isFinite(maxTs) || maxTs < minTs) return [];

  const start = new Date(minTs);
  const end = new Date(maxTs);
  let year = start.getUTCFullYear();
  let month = start.getUTCMonth();
  const endYear = end.getUTCFullYear();
  const endMonth = end.getUTCMonth();
  const months: number[] = [];

  while (year < endYear || (year === endYear && month <= endMonth)) {
    const ts = Date.UTC(year, month, 1);
    if (ts >= minTs && ts <= maxTs) months.push(ts);
    month += 1;
    if (month > 11) {
      month = 0;
      year += 1;
    }
  }

  if (months.length === 0) return [minTs, maxTs];
  const step = Math.max(1, Math.ceil(months.length / maxTicks));
  return months.filter((_, index) => index % step === 0 || index === months.length - 1);
}

function computeRiskMetrics(series: PricePoint[]): RiskMetrics | null {
  if (series.length < 3) return null;
  const ordered = [...series]
    .filter((item) => Number.isFinite(item.close) && item.close > 0)
    .sort((a, b) => a.date.localeCompare(b.date));
  if (ordered.length < 3) return null;

  const first = ordered[0].close;
  const last = ordered[ordered.length - 1].close;
  const periodReturnPct = first > 0 ? (last / first - 1) * 100 : null;

  let peak = ordered[0].close;
  let maxDrawdownPct = 0;
  for (const point of ordered) {
    peak = Math.max(peak, point.close);
    const drawdown = ((point.close - peak) / peak) * 100;
    maxDrawdownPct = Math.min(maxDrawdownPct, drawdown);
  }

  const dailyReturns: number[] = [];
  for (let index = 1; index < ordered.length; index += 1) {
    const prev = ordered[index - 1].close;
    const curr = ordered[index].close;
    if (prev > 0) dailyReturns.push(curr / prev - 1);
  }
  if (dailyReturns.length < 2) {
    return {
      periodReturnPct,
      maxDrawdownPct,
      annualVolatilityPct: null,
    };
  }

  const mean = dailyReturns.reduce((acc, value) => acc + value, 0) / dailyReturns.length;
  const variance = dailyReturns.reduce((acc, value) => acc + (value - mean) ** 2, 0) / dailyReturns.length;
  const annualVolatilityPct = Math.sqrt(variance) * Math.sqrt(252) * 100;

  return {
    periodReturnPct,
    maxDrawdownPct,
    annualVolatilityPct,
  };
}

function PriceComparisonChart({
  assets,
  seriesByAsset,
}: {
  assets: string[];
  seriesByAsset: Record<string, PricePoint[]>;
}) {
  const appColors = useAppColors();
  const colors = appColors.modules.comparadorSeries;
  const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
    month: '2-digit',
    year: '2-digit',
  });

  const normalizedSeries = useMemo(() => {
    return assets
      .map((asset) => {
        const series = (seriesByAsset[asset] ?? [])
          .filter((point) => Number.isFinite(point.close) && point.close > 0)
          .sort((a, b) => a.date.localeCompare(b.date));
        if (series.length < 2) return null;
        const base = series[0].close;
        const points = series.map((point) => ({
          ts: new Date(point.date).getTime(),
          value: (point.close / base) * 100,
        }));
        return { asset, points };
      })
      .filter(
        (
          item,
        ): item is {
          asset: string;
          points: Array<{ ts: number; value: number }>;
        } => item !== null,
      );
  }, [assets, seriesByAsset]);

  if (normalizedSeries.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary'>
        Sem série de cotação suficiente para os ativos selecionados.
      </Typography>
    );
  }

  const allPoints = normalizedSeries.flatMap((item) => item.points);
  const minTs = Math.min(...allPoints.map((item) => item.ts));
  const maxTs = Math.max(...allPoints.map((item) => item.ts));
  const minValue = Math.min(...allPoints.map((item) => item.value));
  const maxValue = Math.max(...allPoints.map((item) => item.value));
  const monthTicks = buildMonthlyTicks(minTs, maxTs);

  const width = 1000;
  const height = 300;
  const left = 64;
  const right = 960;
  const top = 16;
  const bottom = height - 44;
  const plotWidth = right - left;
  const plotHeight = bottom - top;

  const mapX = (ts: number) => {
    if (maxTs === minTs) return left + plotWidth / 2;
    return left + ((ts - minTs) / (maxTs - minTs)) * plotWidth;
  };
  const mapY = (value: number) => {
    if (maxValue === minValue) return top + plotHeight / 2;
    return top + plotHeight - ((value - minValue) / (maxValue - minValue)) * plotHeight;
  };

  return (
    <Box>
      <svg viewBox={`0 0 ${width} ${height}`} width='100%' role='img'>
        <title>Comparação de cotação normalizada</title>
        <rect x={left} y={top} width={plotWidth} height={plotHeight} fill='transparent' stroke='hsl(var(--border))' />
        {Array.from({ length: 5 }).map((_, index) => {
          const ratio = index / 4;
          const y = top + ratio * plotHeight;
          const value = maxValue - ratio * (maxValue - minValue);
          return (
            <g key={`grid-${index}`}>
              <line x1={left} y1={y} x2={right} y2={y} stroke='hsl(var(--border))' strokeDasharray='4 4' />
              <text x={left - 8} y={y + 4} textAnchor='end' fontSize='12' fill='hsl(var(--muted-foreground))'>
                {NUMBER_FORMATTER.format(value)}
              </text>
            </g>
          );
        })}
        {monthTicks.map((tickTs) => {
          const x = mapX(tickTs);
          return (
            <g key={`month-${tickTs}`}>
              <line
                x1={x}
                y1={top}
                x2={x}
                y2={bottom}
                stroke='hsl(var(--border))'
                strokeDasharray='2 4'
                opacity={0.6}
              />
              <text x={x} y={height - 12} textAnchor='middle' fontSize='12' fill='hsl(var(--muted-foreground))'>
                {dateFormatter.format(new Date(tickTs))}
              </text>
            </g>
          );
        })}

        {normalizedSeries.map((series, index) => (
          <polyline
            key={series.asset}
            points={series.points.map((point) => `${mapX(point.ts)},${mapY(point.value)}`).join(' ')}
            fill='none'
            stroke={colors[index % colors.length]}
            strokeWidth='2.2'
          />
        ))}
      </svg>
      <Stack direction='row' spacing={1} useFlexGap flexWrap='wrap' sx={{ mt: 0.8 }}>
        {normalizedSeries.map((series, index) => (
          <Chip
            key={`legend-${series.asset}`}
            size='small'
            variant='outlined'
            label={series.asset}
            sx={{
              borderColor: colors[index % colors.length],
              color: colors[index % colors.length],
              fontWeight: 700,
            }}
          />
        ))}
      </Stack>
    </Box>
  );
}

function DividendComparisonChart({
  assets,
  seriesByAsset,
}: {
  assets: string[];
  seriesByAsset: Record<string, DividendPoint[]>;
}) {
  const appColors = useAppColors();
  const colors = appColors.modules.comparadorSeries;
  const monthFormatter = new Intl.DateTimeFormat('pt-BR', {
    month: '2-digit',
    year: '2-digit',
  });

  const monthly = useMemo(() => {
    const buckets = new Map<string, { ts: number; values: Record<string, number> }>();
    for (const asset of assets) {
      for (const point of seriesByAsset[asset] ?? []) {
        const ts = new Date(point.date).getTime();
        if (!Number.isFinite(ts)) continue;
        const date = new Date(ts);
        const monthTs = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1);
        const key = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
        const current = buckets.get(key) ?? { ts: monthTs, values: {} };
        current.values[asset] = (current.values[asset] ?? 0) + point.amount;
        buckets.set(key, current);
      }
    }

    return Array.from(buckets.entries())
      .map(([key, value]) => ({ key, ...value }))
      .sort((a, b) => a.ts - b.ts)
      .slice(-24);
  }, [assets, seriesByAsset]);

  if (monthly.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary'>
        Sem série de dividendos suficiente para os ativos selecionados.
      </Typography>
    );
  }

  const width = 1000;
  const height = 300;
  const left = 64;
  const right = 960;
  const top = 16;
  const bottom = height - 44;
  const plotWidth = right - left;
  const plotHeight = bottom - top;
  const maxValue = Math.max(...monthly.flatMap((bucket) => assets.map((asset) => bucket.values[asset] ?? 0)), 0.000001);
  const minTs = monthly[0].ts;
  const maxTs = monthly[monthly.length - 1].ts;
  const monthTicks = buildMonthlyTicks(minTs, maxTs);

  const groupSlot = plotWidth / monthly.length;
  const groupInnerWidth = Math.max(16, groupSlot * 0.84);
  const barGap = 2;
  const barWidth = Math.max(2, Math.min(14, (groupInnerWidth - barGap * (assets.length - 1)) / assets.length));

  const mapY = (value: number) => top + plotHeight - (value / maxValue) * plotHeight;
  const mapX = (ts: number) => {
    if (maxTs === minTs) return left + plotWidth / 2;
    return left + ((ts - minTs) / (maxTs - minTs)) * plotWidth;
  };

  return (
    <Box>
      <svg viewBox={`0 0 ${width} ${height}`} width='100%' role='img'>
        <title>Comparação de dividendos por período</title>
        <rect x={left} y={top} width={plotWidth} height={plotHeight} fill='transparent' stroke='hsl(var(--border))' />
        {Array.from({ length: 5 }).map((_, index) => {
          const ratio = index / 4;
          const y = top + ratio * plotHeight;
          const value = maxValue - ratio * maxValue;
          return (
            <g key={`grid-${index}`}>
              <line x1={left} y1={y} x2={right} y2={y} stroke='hsl(var(--border))' strokeDasharray='4 4' />
              <text x={left - 8} y={y + 4} textAnchor='end' fontSize='12' fill='hsl(var(--muted-foreground))'>
                {NUMBER_FORMATTER.format(value)}
              </text>
            </g>
          );
        })}

        {monthly.map((bucket, monthIndex) => {
          const groupLeft = left + monthIndex * groupSlot + (groupSlot - groupInnerWidth) / 2;
          return (
            <g key={bucket.key}>
              {assets.map((asset, assetIndex) => {
                const amount = bucket.values[asset] ?? 0;
                const barHeight = (amount / maxValue) * plotHeight;
                const x = groupLeft + assetIndex * (barWidth + barGap);
                const y = mapY(amount);
                return (
                  <rect
                    key={`${bucket.key}-${asset}`}
                    x={x}
                    y={y}
                    width={barWidth}
                    height={Math.max(1, barHeight)}
                    fill={colors[assetIndex % colors.length]}
                    rx='1.5'
                  />
                );
              })}
            </g>
          );
        })}
        {monthTicks.map((tickTs) => {
          const x = mapX(tickTs);
          return (
            <g key={`month-div-${tickTs}`}>
              <line
                x1={x}
                y1={top}
                x2={x}
                y2={bottom}
                stroke='hsl(var(--border))'
                strokeDasharray='2 4'
                opacity={0.6}
              />
              <text x={x} y={height - 12} textAnchor='middle' fontSize='12' fill='hsl(var(--muted-foreground))'>
                {monthFormatter.format(new Date(tickTs))}
              </text>
            </g>
          );
        })}
      </svg>

      <Stack direction='row' spacing={1} useFlexGap flexWrap='wrap' sx={{ mt: 0.6 }}>
        {assets.map((asset, index) => (
          <Chip
            key={`legend-div-${asset}`}
            size='small'
            variant='outlined'
            label={asset}
            sx={{
              borderColor: colors[index % colors.length],
              color: colors[index % colors.length],
              fontWeight: 700,
            }}
          />
        ))}
      </Stack>
    </Box>
  );
}

function TotalReturnComparisonChart({
  assets,
  priceSeriesByAsset,
  dividendSeriesByAsset,
}: {
  assets: string[];
  priceSeriesByAsset: Record<string, PricePoint[]>;
  dividendSeriesByAsset: Record<string, DividendPoint[]>;
}) {
  const appColors = useAppColors();
  const colors = appColors.modules.comparadorSeries;
  const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
    month: '2-digit',
    year: '2-digit',
  });

  const normalizedSeries = useMemo(() => {
    return assets
      .map((asset) => {
        const priceSeries = (priceSeriesByAsset[asset] ?? [])
          .filter((point) => Number.isFinite(point.close) && point.close > 0)
          .sort((a, b) => a.date.localeCompare(b.date));
        if (priceSeries.length < 2) return null;

        const dividendSeries = (dividendSeriesByAsset[asset] ?? [])
          .filter((point) => Number.isFinite(point.amount) && point.amount >= 0)
          .sort((a, b) => a.date.localeCompare(b.date));

        let dividendIndex = 0;
        let cumulativeDividends = 0;
        const base = priceSeries[0].close;
        const points = priceSeries.map((pricePoint) => {
          const ts = new Date(pricePoint.date).getTime();
          while (dividendIndex < dividendSeries.length) {
            const divTs = new Date(dividendSeries[dividendIndex].date).getTime();
            if (!Number.isFinite(divTs) || divTs > ts) break;
            cumulativeDividends += dividendSeries[dividendIndex].amount;
            dividendIndex += 1;
          }
          return {
            ts,
            value: ((pricePoint.close + cumulativeDividends) / base) * 100,
          };
        });

        return { asset, points };
      })
      .filter(
        (
          item,
        ): item is {
          asset: string;
          points: Array<{ ts: number; value: number }>;
        } => item !== null,
      );
  }, [assets, dividendSeriesByAsset, priceSeriesByAsset]);

  if (normalizedSeries.length === 0) {
    return (
      <Typography variant='body2' color='text.secondary'>
        Sem série suficiente para calcular cotação + dividendos acumulados.
      </Typography>
    );
  }

  const allPoints = normalizedSeries.flatMap((item) => item.points);
  const minTs = Math.min(...allPoints.map((item) => item.ts));
  const maxTs = Math.max(...allPoints.map((item) => item.ts));
  const minValue = Math.min(...allPoints.map((item) => item.value));
  const maxValue = Math.max(...allPoints.map((item) => item.value));
  const monthTicks = buildMonthlyTicks(minTs, maxTs);

  const width = 1000;
  const height = 300;
  const left = 64;
  const right = 960;
  const top = 16;
  const bottom = height - 44;
  const plotWidth = right - left;
  const plotHeight = bottom - top;

  const mapX = (ts: number) => {
    if (maxTs === minTs) return left + plotWidth / 2;
    return left + ((ts - minTs) / (maxTs - minTs)) * plotWidth;
  };
  const mapY = (value: number) => {
    if (maxValue === minValue) return top + plotHeight / 2;
    return top + plotHeight - ((value - minValue) / (maxValue - minValue)) * plotHeight;
  };

  return (
    <Box>
      <svg viewBox={`0 0 ${width} ${height}`} width='100%' role='img'>
        <title>Comparação de cotação com dividendos acumulados</title>
        <rect x={left} y={top} width={plotWidth} height={plotHeight} fill='transparent' stroke='hsl(var(--border))' />
        {Array.from({ length: 5 }).map((_, index) => {
          const ratio = index / 4;
          const y = top + ratio * plotHeight;
          const value = maxValue - ratio * (maxValue - minValue);
          return (
            <g key={`grid-${index}`}>
              <line x1={left} y1={y} x2={right} y2={y} stroke='hsl(var(--border))' strokeDasharray='4 4' />
              <text x={left - 8} y={y + 4} textAnchor='end' fontSize='12' fill='hsl(var(--muted-foreground))'>
                {NUMBER_FORMATTER.format(value)}
              </text>
            </g>
          );
        })}
        {monthTicks.map((tickTs) => {
          const x = mapX(tickTs);
          return (
            <g key={`month-total-${tickTs}`}>
              <line
                x1={x}
                y1={top}
                x2={x}
                y2={bottom}
                stroke='hsl(var(--border))'
                strokeDasharray='2 4'
                opacity={0.6}
              />
              <text x={x} y={height - 12} textAnchor='middle' fontSize='12' fill='hsl(var(--muted-foreground))'>
                {dateFormatter.format(new Date(tickTs))}
              </text>
            </g>
          );
        })}

        {normalizedSeries.map((series, index) => (
          <polyline
            key={series.asset}
            points={series.points.map((point) => `${mapX(point.ts)},${mapY(point.value)}`).join(' ')}
            fill='none'
            stroke={colors[index % colors.length]}
            strokeWidth='2.2'
          />
        ))}
      </svg>
      <Stack direction='row' spacing={1} useFlexGap flexWrap='wrap' sx={{ mt: 0.8 }}>
        {normalizedSeries.map((series, index) => (
          <Chip
            key={`legend-total-${series.asset}`}
            size='small'
            variant='outlined'
            label={series.asset}
            sx={{
              borderColor: colors[index % colors.length],
              color: colors[index % colors.length],
              fontWeight: 700,
            }}
          />
        ))}
      </Stack>
    </Box>
  );
}

function QuoteRangeSelector({ value, onChange }: { value: QuoteRangeId; onChange: (value: QuoteRangeId) => void }) {
  const appColors = useAppColors();
  return (
    <Box sx={{ mb: 0.9 }}>
      <Typography variant='subtitle2' fontWeight={700} sx={{ mb: 0.7 }}>
        Período do gráfico
      </Typography>
      <Stack direction='row' spacing={0.8} flexWrap='wrap' useFlexGap justifyContent='flex-end'>
        {QUOTE_RANGE_OPTIONS.map((item) => (
          <Button
            key={item.id}
            size='small'
            variant={value === item.id ? 'contained' : 'outlined'}
            onClick={() => onChange(item.id)}
            sx={{
              minWidth: 52,
              fontWeight: 700,
              ...(value === item.id
                ? {
                    bgcolor: appColors.actions.range.selectedBg,
                    borderColor: appColors.actions.range.selectedBorder,
                    color: appColors.actions.range.selectedText,
                    '&:hover': {
                      bgcolor: appColors.actions.range.selectedHoverBg,
                      borderColor: appColors.actions.range.selectedHoverBorder,
                    },
                  }
                : {
                    color: appColors.actions.range.unselectedText,
                    borderColor: appColors.actions.range.unselectedBorder,
                    '&:hover': {
                      color: appColors.actions.range.unselectedHoverText,
                      borderColor: appColors.actions.range.unselectedHoverBorder,
                      backgroundColor: appColors.actions.range.unselectedHoverBg,
                    },
                  }),
            }}
          >
            {item.label}
          </Button>
        ))}
      </Stack>
    </Box>
  );
}

function VerticalPercentIndicator({ value, label }: { value: number | null; label: string }) {
  const appColors = useAppColors();
  if (value === null) {
    return <Typography variant='body2'>-</Typography>;
  }

  const clamped = Math.max(0, Math.min(100, value));

  return (
    <Stack direction='row' spacing={1} alignItems='center'>
      <Box
        sx={{
          width: 18,
          height: 58,
          borderRadius: 999,
          border: '1px solid hsl(var(--border))',
          backgroundColor: 'hsl(var(--muted) / 0.38)',
          display: 'flex',
          alignItems: 'flex-end',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: `${clamped}%`,
            background: `linear-gradient(180deg, ${appColors.semantic.info} 0%, ${appColors.semantic.infoStrong} 100%)`,
            transition: 'height 0.2s ease',
          }}
        />
      </Box>
      <Typography variant='body2' sx={{ fontWeight: 600, fontSize: 12 }}>
        {label}
      </Typography>
    </Stack>
  );
}

function classifyQuantitativeBadgeWithColors(
  colors: AppColors,
  indicatorKey: string,
  row: InstrumentRow | null,
  value: number | null,
): {
  label: string;
  color: string;
  accent: string;
  score: number;
} {
  const neutral = {
    label: 'Não informado',
    color: colors.indicators.label.muted,
    accent: colors.semantic.slate,
    score: 0,
  };
  if (value === null || !Number.isFinite(value)) return neutral;

  if (indicatorKey === 'selic_sensitivity') {
    const label = row?.selicSensitivityLabel ?? 'Não informado';
    const normalized = label.trim().toUpperCase();
    const color =
      normalized === 'ALTA'
        ? colors.indicators.label.high
        : normalized === 'MEDIA'
          ? colors.indicators.label.medium
          : normalized === 'BAIXA'
            ? colors.indicators.label.low
            : colors.indicators.label.muted;
    return {
      label,
      color,
      accent: colors.indicators.accent.selic,
      score: Math.max(0, Math.min(100, value)),
    };
  }

  if (indicatorKey === 'leverage_index') {
    const label = row?.leverageIndexLabel ?? 'Não informado';
    const normalized = label.trim().toUpperCase();
    const color =
      normalized === 'ALTA'
        ? colors.indicators.label.high
        : normalized === 'MEDIA'
          ? colors.indicators.label.medium
          : normalized === 'BAIXA'
            ? colors.indicators.label.low
            : colors.indicators.label.muted;
    return {
      label,
      color,
      accent: colors.indicators.accent.leverage,
      score: Math.max(0, Math.min(100, value)),
    };
  }

  if (indicatorKey === 'distance_12m_high') {
    const label = value <= 5 ? 'PRÓXIMO DA MÁXIMA' : value <= 20 ? 'DISTÂNCIA MODERADA' : 'DISTANTE DA MÁXIMA';
    const color =
      value <= 5
        ? colors.indicators.distance.near
        : value <= 20
          ? colors.indicators.distance.medium
          : colors.indicators.distance.far;
    return {
      label,
      color,
      accent: colors.indicators.accent.distance,
      score: Math.max(0, Math.min(100, value)),
    };
  }

  if (indicatorKey === 'vacancia') {
    const label = value < 5 ? 'BAIXA' : value < 12 ? 'MEDIA' : 'ALTA';
    const color =
      value < 5
        ? colors.indicators.label.low
        : value < 12
          ? colors.indicators.label.medium
          : colors.indicators.label.high;
    return {
      label,
      color,
      accent: colors.indicators.accent.vacancy,
      score: Math.max(0, Math.min(100, value)),
    };
  }

  if (indicatorKey === 'inadimplencia') {
    const label = value < 2 ? 'BAIXA' : value < 5 ? 'MEDIA' : 'ALTA';
    const color =
      value < 2
        ? colors.indicators.label.low
        : value < 5
          ? colors.indicators.label.medium
          : colors.indicators.label.high;
    return {
      label,
      color,
      accent: colors.indicators.accent.delinquency,
      score: Math.max(0, Math.min(100, value)),
    };
  }

  if (indicatorKey === 'vol_21d') {
    const label = value < 15 ? 'BAIXA' : value < 25 ? 'MEDIA' : 'ALTA';
    const color =
      value < 15
        ? colors.indicators.label.low
        : value < 25
          ? colors.indicators.label.medium
          : colors.indicators.label.high;
    return {
      label,
      color,
      accent: colors.indicators.accent.selic,
      score: Math.max(0, Math.min(100, value)),
    };
  }

  const concentrationLabel = concentrationBand(value);
  const concentrationColor =
    concentrationLabel === 'Baixa concentração'
      ? colors.indicators.label.low
      : concentrationLabel === 'Concentração moderada'
        ? colors.indicators.label.medium
        : concentrationLabel === 'Muito concentrado'
          ? colors.indicators.label.high
          : colors.indicators.label.muted;
  return {
    label: concentrationLabel.toUpperCase(),
    color: concentrationColor,
    accent: colors.indicators.accent.selic,
    score: Math.max(0, Math.min(100, value)),
  };
}

function QuantitativeIndicatorCell({
  indicatorKey,
  row,
  rawValue,
  label,
}: {
  indicatorKey: string;
  row: InstrumentRow | null;
  rawValue: number | null;
  label: string;
}) {
  const appColors = useAppColors();
  const badge = classifyQuantitativeBadgeWithColors(appColors, indicatorKey, row, rawValue);

  return (
    <Box
      sx={{
        p: 1,
        border: '1px solid hsl(var(--border))',
        borderRadius: 1.2,
        borderTop: `3px solid ${badge.accent}`,
        bgcolor: 'hsl(var(--card))',
        overflow: 'hidden',
      }}
    >
      <Stack direction='row' justifyContent='space-between' alignItems='baseline' sx={{ mb: 0.65, minWidth: 0 }}>
        <Typography
          variant='body1'
          fontWeight={800}
          sx={{
            fontSize: 22,
            lineHeight: 1.1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            pr: 0.7,
          }}
        >
          {label}
        </Typography>
        <Chip
          size='small'
          label={badge.label}
          variant='outlined'
          sx={{
            height: 20,
            fontWeight: 700,
            fontSize: 11,
            color: badge.color,
            borderColor: `${badge.color}66`,
            backgroundColor: `${badge.color}14`,
            maxWidth: '60%',
            '& .MuiChip-label': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            },
          }}
        />
      </Stack>
      <LinearProgress
        variant='determinate'
        value={badge.score}
        sx={{
          height: 8,
          borderRadius: 999,
          backgroundColor: 'hsl(var(--muted))',
          '& .MuiLinearProgress-bar': { backgroundColor: badge.accent },
        }}
      />
      <Typography variant='caption' color='text.secondary' sx={{ mt: 0.55, display: 'block', fontSize: 11 }}>
        Escala de 0% a 100%
      </Typography>
    </Box>
  );
}

function MarketIndicatorCell({
  label,
  score,
  accent,
  emphasize = false,
  secondaryText,
}: {
  label: string;
  score: number;
  accent?: string;
  emphasize?: boolean;
  secondaryText?: string | null;
}) {
  const appColors = useAppColors();
  const effectiveAccent = accent ?? appColors.indicators.accent.leverage;
  return (
    <Box
      sx={{
        p: 1,
        border: '1px solid hsl(var(--border))',
        borderRadius: 1.2,
        borderTop: '3px solid hsl(var(--border))',
        bgcolor: emphasize ? 'hsl(var(--muted) / 0.22)' : 'hsl(var(--card))',
        boxShadow: emphasize ? `inset 0 0 0 1px ${effectiveAccent}33` : 'none',
      }}
    >
      <Typography variant='body1' fontWeight={800} sx={{ fontSize: emphasize ? 23 : 22, lineHeight: 1.1, mb: 0.7 }}>
        {label}
      </Typography>
      {secondaryText ? (
        <Typography
          variant='caption'
          sx={{
            display: 'block',
            mt: -0.45,
            mb: 0.6,
            color: 'text.secondary',
            fontWeight: 700,
            fontSize: 11.5,
          }}
        >
          {secondaryText}
        </Typography>
      ) : null}
      <LinearProgress
        variant='determinate'
        value={Math.max(0, Math.min(100, score))}
        sx={{
          height: 8,
          borderRadius: 999,
          backgroundColor: 'hsl(var(--muted))',
          '& .MuiLinearProgress-bar': { backgroundColor: effectiveAccent },
        }}
      />
    </Box>
  );
}

function VerticalSignedIndicator({
  value,
  min,
  max,
  label,
}: {
  value: number | null;
  min: number;
  max: number;
  label: string;
}) {
  const appColors = useAppColors();
  if (value === null || !Number.isFinite(value) || max <= min || min >= 0 || max <= 0) {
    return <Typography variant='body2'>{label}</Typography>;
  }

  const clamped = Math.max(min, Math.min(max, value));
  const zeroRatio = (max - 0) / (max - min);
  const zeroPercent = Math.max(0, Math.min(100, zeroRatio * 100));
  const positiveHeight = clamped > 0 ? (clamped / max) * zeroPercent : 0;
  const negativeHeight = clamped < 0 ? (Math.abs(clamped) / Math.abs(min)) * (100 - zeroPercent) : 0;

  return (
    <Stack direction='row' spacing={1} alignItems='center'>
      <Box
        sx={{
          width: 18,
          height: 58,
          borderRadius: 999,
          border: '1px solid hsl(var(--border))',
          backgroundColor: 'hsl(var(--muted) / 0.38)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: `${zeroPercent}%`,
            borderTop: '1px solid hsl(var(--border))',
            opacity: 0.8,
          }}
        />
        {positiveHeight > 0 ? (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: `${100 - zeroPercent}%`,
              height: `${positiveHeight}%`,
              background: `linear-gradient(180deg, ${appColors.semantic.info} 0%, ${appColors.semantic.infoStrong} 100%)`,
            }}
          />
        ) : null}
        {negativeHeight > 0 ? (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: `${zeroPercent}%`,
              height: `${negativeHeight}%`,
              background: `linear-gradient(180deg, ${appColors.semantic.danger} 0%, ${appColors.semantic.dangerStrong} 100%)`,
            }}
          />
        ) : null}
      </Box>
      <Typography variant='body2' sx={{ fontWeight: 600, fontSize: 12 }}>
        {label}
      </Typography>
    </Stack>
  );
}

function IndicatorLabel({
  label,
  description,
  showCheckbox = false,
  checked = true,
  onToggle,
  emphasize = false,
}: {
  label: string;
  description: string;
  showCheckbox?: boolean;
  checked?: boolean;
  onToggle?: () => void;
  emphasize?: boolean;
}) {
  return (
    <Stack direction='row' spacing={0.55} alignItems='flex-start' sx={{ minWidth: 0 }}>
      {showCheckbox ? <Checkbox size='small' checked={checked} onChange={() => onToggle?.()} sx={{ p: 0.25 }} /> : null}
      <Typography
        variant='body2'
        sx={{
          fontWeight: emphasize ? 800 : 700,
          fontSize: emphasize ? 12.5 : 12,
          lineHeight: 1.25,
          minWidth: 0,
          whiteSpace: 'normal',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
          flex: 1,
          color: emphasize ? 'hsl(var(--foreground))' : 'inherit',
        }}
      >
        {label}
      </Typography>
      <Tooltip title={description} arrow placement='top'>
        <Box
          sx={{
            display: 'inline-flex',
            color: 'hsl(var(--muted-foreground))',
            cursor: 'help',
            mt: 0.1,
            flexShrink: 0,
          }}
        >
          <CircleHelp size={14} />
        </Box>
      </Tooltip>
    </Stack>
  );
}

function SectionHeaderRow({
  title,
  count,
  collapsed,
  onToggle,
  showCheckbox = false,
  checked = false,
  indeterminate = false,
  onToggleCheckbox,
}: {
  title: string;
  count?: number;
  collapsed: boolean;
  onToggle: () => void;
  showCheckbox?: boolean;
  checked?: boolean;
  indeterminate?: boolean;
  onToggleCheckbox?: () => void;
}) {
  return (
    <Box
      sx={{
        gridColumn: '1 / -1',
        p: 1,
        borderTop: '1px solid hsl(var(--border))',
        bgcolor: 'hsl(var(--muted) / 0.2)',
        fontWeight: 800,
        cursor: 'pointer',
      }}
      onClick={onToggle}
      role='button'
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle();
        }
      }}
    >
      <Stack direction='row' spacing={0.7} alignItems='center'>
        {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
        {showCheckbox ? (
          <Checkbox
            size='small'
            checked={checked}
            indeterminate={indeterminate}
            onClick={(event) => event.stopPropagation()}
            onChange={() => onToggleCheckbox?.()}
            sx={{ p: 0.2 }}
          />
        ) : null}
        <Typography variant='body2' sx={{ fontWeight: 700, fontSize: 13 }}>
          {title}
        </Typography>
        {typeof count === 'number' ? (
          <Chip size='small' label={count} variant='outlined' sx={{ height: 19, fontWeight: 700, fontSize: 11 }} />
        ) : null}
      </Stack>
    </Box>
  );
}

function classifyRiskLevelWithColors(
  colors: AppColors,
  indicatorKey: string,
  value: number | null,
): {
  label: 'Baixo' | 'Médio' | 'Alto';
  bg: string;
  color: string;
} {
  const mediumBg = `${colors.modules.risk.medium}1f`;
  const highBg = `${colors.modules.risk.bad}1f`;
  const lowBg = `${colors.modules.risk.good}1f`;
  if (value === null || !Number.isFinite(value)) {
    return {
      label: 'Médio',
      bg: mediumBg,
      color: colors.semantic.warningStrong,
    };
  }

  if (indicatorKey === 'vol_realizada') {
    if (value >= 30) return { label: 'Alto', bg: highBg, color: colors.semantic.dangerStrong };
    if (value >= 18)
      return {
        label: 'Médio',
        bg: mediumBg,
        color: colors.semantic.warningStrong,
      };
    return { label: 'Baixo', bg: lowBg, color: colors.semantic.successStrong };
  }

  if (indicatorKey === 'max_drawdown') {
    if (value <= -25) return { label: 'Alto', bg: highBg, color: colors.semantic.dangerStrong };
    if (value <= -12)
      return {
        label: 'Médio',
        bg: mediumBg,
        color: colors.semantic.warningStrong,
      };
    return { label: 'Baixo', bg: lowBg, color: colors.semantic.successStrong };
  }

  if (indicatorKey === 'retorno_periodo') {
    if (value < 0) return { label: 'Alto', bg: highBg, color: colors.semantic.dangerStrong };
    if (value < 8)
      return {
        label: 'Médio',
        bg: mediumBg,
        color: colors.semantic.warningStrong,
      };
    return { label: 'Baixo', bg: lowBg, color: colors.semantic.successStrong };
  }

  return {
    label: 'Médio',
    bg: mediumBg,
    color: colors.semantic.warningStrong,
  };
}

function concentrationBand(value: number | null) {
  if (value === null || !Number.isFinite(value)) return 'Não informado';
  if (value >= 40) return 'Muito concentrado';
  if (value >= 20) return 'Concentração moderada';
  return 'Baixa concentração';
}

function riskBarScale(indicatorKey: string): { min: number; max: number } {
  if (indicatorKey === 'vol_realizada') return { min: 0, max: 50 };
  if (indicatorKey === 'max_drawdown') return { min: -40, max: 40 };
  if (indicatorKey === 'retorno_periodo') return { min: -30, max: 30 };
  return { min: 0, max: 100 };
}

function marketBarScale(indicatorKey: string): { min: number; max: number } {
  if (indicatorKey === 'liquidez_30d') return { min: 0, max: 25_000_000 };
  if (indicatorKey === 'dy_mes') return { min: 0, max: 2.5 };
  if (indicatorKey === 'pvp_discount_hist') return { min: -30, max: 30 };
  return { min: 0, max: 100 };
}

function fundamentalsBarScale(indicatorKey: string): {
  min: number;
  max: number;
} {
  if (indicatorKey === 'preco_atual') return { min: 0, max: 300 };
  if (indicatorKey === 'ultimo_dividendo') return { min: 0, max: 5 };
  if (indicatorKey === 'vp_por_cota') return { min: 0, max: 300 };
  if (indicatorKey === 'patrimonio') return { min: 0, max: 10_000_000_000 };
  if (indicatorKey === 'pvp') return { min: 0.5, max: 1.5 };
  if (indicatorKey === 'valor_mercado') return { min: 0, max: 10_000_000_000 };
  if (indicatorKey === 'dy12m') return { min: 0, max: 25 };
  if (indicatorKey === 'dividendos12m') return { min: 0, max: 25 };
  if (indicatorKey === 'numero_cotistas') return { min: 0, max: 500_000 };
  if (indicatorKey === 'numero_cotas') return { min: 0, max: 1_000_000_000 };
  return { min: 0, max: 100 };
}

function formatFundamentalsVisualLabel(indicator: IndicatorDefinition, rawValue: number | null): string {
  if (rawValue === null || !Number.isFinite(rawValue)) return '-';
  if (indicator.key !== 'pvp') return indicator.format(rawValue);
  const ratio = rawValue;
  if (Math.abs(ratio - 1) <= 0.0001) return `${formatRatio(ratio)} · No VP`;
  const diffPct = Math.abs((ratio - 1) * 100);
  const diffLabel = `${PERCENT_FORMATTER.format(diffPct)}%`;
  if (ratio > 1) return `${formatRatio(ratio)} · Ágio ${diffLabel}`;
  return `${formatRatio(ratio)} · Deságio ${diffLabel}`;
}

function formatFundamentalsSecondary(indicator: IndicatorDefinition, rawValue: number | null): string | null {
  if (rawValue === null || !Number.isFinite(rawValue)) return null;
  if (indicator.key !== 'pvp') return null;
  const ratio = rawValue;
  if (Math.abs(ratio - 1) <= 0.0001) return 'No VP';
  const diffPct = Math.abs((ratio - 1) * 100);
  const diffLabel = `${PERCENT_FORMATTER.format(diffPct)}%`;
  if (ratio > 1) return `Ágio ${diffLabel}`;
  return `Deságio ${diffLabel}`;
}

function fundamentalsIndicatorAccent(appColors: AppColors, indicatorKey: string, rawValue: number | null): string {
  if (indicatorKey === 'pvp') {
    if (rawValue === null || !Number.isFinite(rawValue)) return appColors.semantic.slate;
    if (rawValue <= 0.95) return appColors.semantic.successStrong;
    if (rawValue <= 1.05) return appColors.semantic.warningStrong;
    return appColors.semantic.dangerStrong;
  }
  if (['ultimo_dividendo', 'dy12m', 'dividendos12m'].includes(indicatorKey)) {
    return appColors.semantic.successStrong;
  }
  if (['numero_cotistas', 'numero_cotas'].includes(indicatorKey)) {
    return appColors.semantic.slate;
  }
  return appColors.semantic.infoStrong;
}

function marketIndicatorAccent(appColors: AppColors, indicatorKey: string, rawValue: number | null): string {
  if (indicatorKey === 'liquidez_30d') return appColors.semantic.infoStrong;
  if (indicatorKey === 'dy_mes') return appColors.semantic.successStrong;
  if (indicatorKey === 'pvp_discount_hist') {
    if (rawValue === null || !Number.isFinite(rawValue)) return appColors.semantic.slate;
    if (rawValue < 0) return appColors.semantic.successStrong;
    if (rawValue <= 5) return appColors.semantic.warningStrong;
    return appColors.semantic.dangerStrong;
  }
  return appColors.semantic.slate;
}

function sortIndicatorsByOrder(
  indicators: IndicatorDefinition[],
  orderedKeys: readonly string[],
): IndicatorDefinition[] {
  const orderMap = new Map(orderedKeys.map((key, index) => [key, index]));
  return [...indicators].sort((a, b) => {
    const aIndex = orderMap.get(a.key) ?? Number.MAX_SAFE_INTEGER;
    const bIndex = orderMap.get(b.key) ?? Number.MAX_SAFE_INTEGER;
    if (aIndex !== bIndex) return aIndex - bIndex;
    return a.label.localeCompare(b.label, 'pt-BR');
  });
}

function normalizeToPercent(value: number | null, min: number, max: number): number | null {
  if (value === null || !Number.isFinite(value)) return null;
  if (max <= min) return null;
  const raw = ((value - min) / (max - min)) * 100;
  return Math.max(0, Math.min(100, raw));
}

export function ComparadorClient({
  embedded = false,
  initialAssets = EMPTY_ASSETS,
  initialIndicators = EMPTY_INDICATORS,
  showHeading = true,
}: {
  embedded?: boolean;
  initialAssets?: string[];
  initialIndicators?: string[];
  showHeading?: boolean;
}) {
  const appColors = useAppColors();
  const router = useRouter();
  const pathname = usePathname() ?? '/comparador';
  const searchParams = useSearchParams();
  const searchParamsString = searchParams?.toString() ?? '';
  const { tickers, loading: loadingInstruments } = useInstrumentCache();

  const [isFundPickerOpen, setIsFundPickerOpen] = useState(false);
  const [isIndicatorSelectionMode, setIsIndicatorSelectionMode] = useState(false);
  const [draftIndicatorKeys, setDraftIndicatorKeys] = useState<string[]>([]);
  const [shareToastOpen, setShareToastOpen] = useState(false);
  const [quoteRange, setQuoteRange] = useState<QuoteRangeId>('1A');
  const [embeddedAssets, setEmbeddedAssets] = useState(() => parseAssets(initialAssets.join('.')));
  const [embeddedIndicatorKeys, setEmbeddedIndicatorKeys] = useState<string[]>(() => {
    const selected = initialIndicators.filter((key) => ALL_SELECTABLE_KEYS.includes(key));
    return selected.length > 0 ? Array.from(new Set(selected)) : ALL_SELECTABLE_KEYS;
  });
  const [collapsedSections, setCollapsedSections] = useState<Record<SectionKey, boolean>>(
    () =>
      getDefaultCollapsedSections({
        embedded,
        initialIndicators,
        sectionKeys: SECTION_SELECTABLE_KEYS,
      }) as Record<SectionKey, boolean>,
  );

  const selectedAssets = useMemo(() => {
    if (embedded) return parseAssets(embeddedAssets.join('.'));
    const params = new URLSearchParams(searchParamsString);
    const fromQuery = parseAssets(params.get(ASSETS_QUERY_KEY));
    if (fromQuery.length > 0) return fromQuery;
    const fromPath = parseAssetsFromPathname(pathname);
    if (fromPath.length > 0) return fromPath;
    return parseAssets(initialAssets.join('.'));
  }, [embedded, embeddedAssets, initialAssets, pathname, searchParamsString]);
  const selectedIndicators = useMemo(
    () =>
      embedded
        ? embeddedIndicatorKeys
        : parseIndicators(new URLSearchParams(searchParamsString).get(INDICATORS_QUERY_KEY)),
    [embedded, embeddedIndicatorKeys, searchParamsString],
  );
  const hasIndicatorsParam = embedded || new URLSearchParams(searchParamsString).has(INDICATORS_QUERY_KEY);

  const fullComparatorHref = useMemo(() => {
    const path = buildComparadorPathFromAssets(initialAssets);
    const selected = Array.from(new Set(initialIndicators.filter((key) => ALL_SELECTABLE_KEYS.includes(key))));
    if (selected.length === 0 || selected.length === ALL_SELECTABLE_KEYS.length) {
      return path;
    }
    const token = encodeComparatorIndicatorsBitmap(selected);
    return `${path}?${INDICATORS_QUERY_KEY}=${encodeURIComponent(token)}`;
  }, [initialAssets, initialIndicators]);

  const persistedIndicatorKeys = useMemo(
    () => (hasIndicatorsParam ? selectedIndicators : ALL_SELECTABLE_KEYS),
    [hasIndicatorsParam, selectedIndicators],
  );
  const selectedIndicatorKeys = isIndicatorSelectionMode ? draftIndicatorKeys : persistedIndicatorKeys;
  const visibleIndicatorKeys = isIndicatorSelectionMode ? ALL_SELECTABLE_KEYS : persistedIndicatorKeys;

  const updateUrl = useCallback(
    (next: { assets?: string[]; indicators?: string[] }) => {
      if (embedded) {
        if (next.assets) setEmbeddedAssets(next.assets);
        if (next.indicators) setEmbeddedIndicatorKeys(next.indicators);
        return;
      }
      const params = new URLSearchParams(searchParamsString);
      let nextPath = pathname;

      if (next.assets) {
        const normalized = Array.from(new Set(next.assets.map((asset) => toInstrumentLabel(asset)))).slice(
          0,
          MAX_ASSETS,
        );
        if (normalized.length > 0) {
          nextPath = buildComparadorPathFromAssets(normalized);
        } else {
          nextPath = '/comparador';
        }
        params.delete(ASSETS_QUERY_KEY);
      }

      if (next.indicators) {
        const normalized = Array.from(new Set(next.indicators.filter((key) => ALL_SELECTABLE_KEYS.includes(key))));
        if (normalized.length === 0) {
          params.set(INDICATORS_QUERY_KEY, `${INDICATORS_BITMAP_PREFIX}0`);
        } else if (normalized.length === ALL_SELECTABLE_KEYS.length) {
          params.delete(INDICATORS_QUERY_KEY);
        } else {
          params.set(INDICATORS_QUERY_KEY, encodeComparatorIndicatorsBitmap(normalized));
        }
      }

      const query = params.toString();
      router.replace(query ? `${nextPath}?${query}` : nextPath, {
        scroll: false,
      });
    },
    [embedded, pathname, router, searchParamsString],
  );

  const shareableUrl = useMemo(() => {
    const query = searchParamsString;
    if (typeof window === 'undefined') return query ? `${pathname}?${query}` : pathname;
    return query ? `${window.location.origin}${pathname}?${query}` : `${window.location.origin}${pathname}`;
  }, [pathname, searchParamsString]);

  const handleShare = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareableUrl);
      setShareToastOpen(true);
    } catch {
      setShareToastOpen(false);
    }
  }, [shareableUrl]);

  useEffect(() => {
    if (!isIndicatorSelectionMode) {
      setDraftIndicatorKeys(persistedIndicatorKeys);
    }
  }, [isIndicatorSelectionMode, persistedIndicatorKeys]);

  const indicatorBySection = useMemo(() => {
    const filtered = INDICATORS.filter((indicator) => visibleIndicatorKeys.includes(indicator.key));
    const fundamentos = filtered.filter((item) => item.section === 'fundamentos');
    const mercado = filtered.filter((item) => item.section === 'mercado');
    return {
      fundamentos: sortIndicatorsByOrder(fundamentos, FUNDAMENTALS_DISPLAY_ORDER),
      mercado: sortIndicatorsByOrder(mercado, MARKET_DISPLAY_ORDER),
      portfolio: filtered.filter((item) => item.section === 'portfolio'),
      quantitativos: filtered.filter((item) => item.section === 'quantitativos'),
      risco: filtered.filter((item) => item.section === 'risco'),
    };
  }, [visibleIndicatorKeys]);
  const activeCotacaoCharts = useMemo(
    () => COTACAO_CHART_ROWS.filter((row) => visibleIndicatorKeys.includes(row.key)),
    [visibleIndicatorKeys],
  );
  const showQuoteChart = useMemo(
    () => visibleIndicatorKeys.includes('grafico_cotacao_normalizada'),
    [visibleIndicatorKeys],
  );
  const showTotalReturnChart = useMemo(
    () => visibleIndicatorKeys.includes('grafico_cotacao_dividendos'),
    [visibleIndicatorKeys],
  );
  const showDividendChart = useMemo(
    () => visibleIndicatorKeys.includes('grafico_dividendos_periodo'),
    [visibleIndicatorKeys],
  );
  const showPortfolioTop5 = useMemo(() => visibleIndicatorKeys.includes('portfolio_top5'), [visibleIndicatorKeys]);
  const showPortfolioMix = useMemo(() => visibleIndicatorKeys.includes('portfolio_mix'), [visibleIndicatorKeys]);
  const showPortfolioConcentration = useMemo(
    () => visibleIndicatorKeys.includes('portfolio_concentracao'),
    [visibleIndicatorKeys],
  );
  const activeBasicRows = useMemo(
    () => BASIC_DATA_ROWS.filter((row) => visibleIndicatorKeys.includes(row.key)),
    [visibleIndicatorKeys],
  );

  const rowsByAsset = useMemo(() => {
    const byIdCem = new Map(tickers.map((row) => [row.idCem.toUpperCase(), row]));
    return Object.fromEntries(
      selectedAssets.map((asset) => [asset, byIdCem.get(toBaseInstrument(asset)) ?? null]),
    ) as Record<string, InstrumentRow | null>;
  }, [selectedAssets, tickers]);

  const missingAssets = useMemo(
    () => selectedAssets.filter((asset) => rowsByAsset[asset] === null),
    [rowsByAsset, selectedAssets],
  );

  const [fundamentalsByAsset, setFundamentalsByAsset] = useState<Record<string, FundamentalsSnapshot | null>>({});
  const [loadingFundamentals, setLoadingFundamentals] = useState(false);
  const [portfolioHighlightsByAsset, setPortfolioHighlightsByAsset] = useState<
    Record<string, PortfolioHighlightsSnapshot | null>
  >({});
  const [loadingPortfolioHighlights, setLoadingPortfolioHighlights] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (selectedAssets.length === 0) {
        setFundamentalsByAsset({});
        return;
      }

      setLoadingFundamentals(true);
      try {
        const response = await fetch(
          `/api/comparador/fundamentos?ativos=${encodeURIComponent(selectedAssets.join(','))}`,
        );
        if (!response.ok) {
          if (!cancelled) {
            setFundamentalsByAsset(
              Object.fromEntries(selectedAssets.map((asset) => [asset, null])) as Record<
                string,
                FundamentalsSnapshot | null
              >,
            );
          }
          return;
        }

        const data = (await response.json()) as FundamentalsSnapshot[];
        const byIdCem = new Map(data.filter((row) => row.idCem).map((row) => [row.idCem!.toUpperCase(), row]));
        const mapped = Object.fromEntries(
          selectedAssets.map((asset) => [asset, byIdCem.get(toBaseInstrument(asset)) ?? null]),
        ) as Record<string, FundamentalsSnapshot | null>;

        if (!cancelled) setFundamentalsByAsset(mapped);
      } catch {
        if (!cancelled) {
          setFundamentalsByAsset(
            Object.fromEntries(selectedAssets.map((asset) => [asset, null])) as Record<
              string,
              FundamentalsSnapshot | null
            >,
          );
        }
      } finally {
        if (!cancelled) setLoadingFundamentals(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [selectedAssets]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (selectedAssets.length === 0) {
        setPortfolioHighlightsByAsset({});
        return;
      }

      setLoadingPortfolioHighlights(true);
      try {
        const response = await fetch(
          `/api/comparador/portfolio-highlights?ativos=${encodeURIComponent(selectedAssets.join(','))}`,
        );
        if (!response.ok) {
          if (!cancelled) {
            setPortfolioHighlightsByAsset(
              Object.fromEntries(selectedAssets.map((asset) => [asset, null])) as Record<
                string,
                PortfolioHighlightsSnapshot | null
              >,
            );
          }
          return;
        }

        const data = (await response.json()) as PortfolioHighlightsSnapshot[];
        const byIdCem = new Map(data.filter((row) => row.idCem).map((row) => [row.idCem!.toUpperCase(), row]));
        const mapped = Object.fromEntries(
          selectedAssets.map((asset) => [asset, byIdCem.get(toBaseInstrument(asset)) ?? null]),
        ) as Record<string, PortfolioHighlightsSnapshot | null>;

        if (!cancelled) setPortfolioHighlightsByAsset(mapped);
      } catch {
        if (!cancelled) {
          setPortfolioHighlightsByAsset(
            Object.fromEntries(selectedAssets.map((asset) => [asset, null])) as Record<
              string,
              PortfolioHighlightsSnapshot | null
            >,
          );
        }
      } finally {
        if (!cancelled) setLoadingPortfolioHighlights(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [selectedAssets]);

  const [priceSeriesByAsset, setPriceSeriesByAsset] = useState<Record<string, PricePoint[]>>({});
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [dividendSeriesByAsset, setDividendSeriesByAsset] = useState<Record<string, DividendPoint[]>>({});
  const [loadingDividends, setLoadingDividends] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (selectedAssets.length === 0) {
        setPriceSeriesByAsset({});
        return;
      }

      setLoadingPrices(true);
      try {
        const entries = await Promise.all(
          selectedAssets.map(async (asset) => {
            try {
              const response = await fetch(`/api/ticker-price-history/${encodeURIComponent(asset)}`);
              if (!response.ok) return [asset, []] as const;
              const data = (await response.json()) as {
                series?: Array<{ date: string; close: number }>;
              };
              return [asset, data.series ?? []] as const;
            } catch {
              return [asset, []] as const;
            }
          }),
        );
        if (!cancelled) {
          setPriceSeriesByAsset(Object.fromEntries(entries));
        }
      } finally {
        if (!cancelled) setLoadingPrices(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [selectedAssets]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (selectedAssets.length === 0) {
        setDividendSeriesByAsset({});
        return;
      }

      setLoadingDividends(true);
      try {
        const entries = await Promise.all(
          selectedAssets.map(async (asset) => {
            try {
              const response = await fetch(`/api/ticker-dividends-history/${encodeURIComponent(asset)}`);
              if (!response.ok) return [asset, []] as const;
              const data = (await response.json()) as {
                series?: Array<{ date: string; amount: number }>;
              };
              return [asset, data.series ?? []] as const;
            } catch {
              return [asset, []] as const;
            }
          }),
        );
        if (!cancelled) {
          setDividendSeriesByAsset(Object.fromEntries(entries));
        }
      } finally {
        if (!cancelled) setLoadingDividends(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [selectedAssets]);

  const riskByAsset = useMemo(() => {
    const entries = selectedAssets.map(
      (asset) => [asset, computeRiskMetrics(priceSeriesByAsset[asset] ?? [])] as const,
    );
    return Object.fromEntries(entries) as Record<string, RiskMetrics | null>;
  }, [priceSeriesByAsset, selectedAssets]);

  const filteredPriceSeriesByAsset = useMemo(() => {
    const selectedRange = QUOTE_RANGE_OPTIONS.find((item) => item.id === quoteRange);
    const rangeDays = selectedRange?.days;
    if (rangeDays === null || rangeDays === undefined) return priceSeriesByAsset;

    const byAsset = Object.fromEntries(
      selectedAssets.map((asset) => {
        const series = (priceSeriesByAsset[asset] ?? []).sort((a, b) => a.date.localeCompare(b.date));
        if (series.length === 0) return [asset, []] as const;
        const lastTs = new Date(series[series.length - 1].date).getTime();
        if (!Number.isFinite(lastTs)) return [asset, series] as const;
        const thresholdTs = lastTs - rangeDays * DAY_MS;
        const filtered = series.filter((point) => new Date(point.date).getTime() >= thresholdTs);
        return [asset, filtered.length > 0 ? filtered : series] as const;
      }),
    );
    return byAsset as Record<string, PricePoint[]>;
  }, [priceSeriesByAsset, quoteRange, selectedAssets]);

  const filteredDividendSeriesByAsset = useMemo(() => {
    const selectedRange = QUOTE_RANGE_OPTIONS.find((item) => item.id === quoteRange);
    const rangeDays = selectedRange?.days;
    if (rangeDays === null || rangeDays === undefined) return dividendSeriesByAsset;

    const byAsset = Object.fromEntries(
      selectedAssets.map((asset) => {
        const dividends = (dividendSeriesByAsset[asset] ?? []).sort((a, b) => a.date.localeCompare(b.date));
        const prices = filteredPriceSeriesByAsset[asset] ?? [];
        if (prices.length === 0) return [asset, dividends] as const;
        const firstPriceTs = new Date(prices[0].date).getTime();
        const filtered = dividends.filter((point) => new Date(point.date).getTime() >= firstPriceTs);
        return [asset, filtered] as const;
      }),
    );
    return byAsset as Record<string, DividendPoint[]>;
  }, [dividendSeriesByAsset, filteredPriceSeriesByAsset, quoteRange, selectedAssets]);

  const toggleDraftIndicator = useCallback((indicatorKey: string) => {
    setDraftIndicatorKeys((current) => {
      const base = new Set(current);
      if (base.has(indicatorKey)) base.delete(indicatorKey);
      else base.add(indicatorKey);
      return ALL_SELECTABLE_KEYS.filter((key) => base.has(key));
    });
  }, []);

  const toggleIndicatorSelectionMode = useCallback(() => {
    if (!isIndicatorSelectionMode) {
      setDraftIndicatorKeys(persistedIndicatorKeys);
      setIsIndicatorSelectionMode(true);
      return;
    }
    updateUrl({ indicators: draftIndicatorKeys });
    setIsIndicatorSelectionMode(false);
  }, [draftIndicatorKeys, isIndicatorSelectionMode, persistedIndicatorKeys, updateUrl]);
  const toggleSection = useCallback((section: SectionKey) => {
    setCollapsedSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  }, []);
  const sectionCheckState = useCallback(
    (section: SectionKey) => {
      const keys = SECTION_SELECTABLE_KEYS[section] ?? [];
      if (keys.length === 0) return { checked: false, indeterminate: false };
      const selectedCount = keys.filter((key) => selectedIndicatorKeys.includes(key)).length;
      return {
        checked: selectedCount === keys.length,
        indeterminate: selectedCount > 0 && selectedCount < keys.length,
      };
    },
    [selectedIndicatorKeys],
  );
  const toggleSectionDraft = useCallback((section: SectionKey) => {
    const keys = SECTION_SELECTABLE_KEYS[section] ?? [];
    if (keys.length === 0) return;
    setDraftIndicatorKeys((current) => {
      const next = new Set(current);
      const allSelected = keys.every((key) => next.has(key));
      if (allSelected) keys.forEach((key) => next.delete(key));
      else keys.forEach((key) => next.add(key));
      return ALL_SELECTABLE_KEYS.filter((key) => next.has(key));
    });
  }, []);

  const currentInstrumentForPicker = selectedAssets[0] ?? 'NENHUM';
  const zebraRowBg = (index: number) => (index % 2 === 0 ? 'hsl(var(--background))' : 'hsl(var(--muted) / 0.18)');
  const stickyLeftCellSx = {
    position: { xs: 'static', md: 'sticky' },
    left: { md: 0 },
    zIndex: { md: 4 },
    borderRight: '1px solid hsl(var(--border))',
  } as const;
  const sectionCounts = {
    basicos: activeBasicRows.length,
    fundamentos: indicatorBySection.fundamentos.length,
    mercado: indicatorBySection.mercado.length,
    quantitativos: indicatorBySection.quantitativos.length,
    cotacao: activeCotacaoCharts.length,
    risco: indicatorBySection.risco.length,
    portfolio: [
      showPortfolioTop5 ? 1 : 0,
      showPortfolioMix ? 1 : 0,
      showPortfolioConcentration ? 1 : 0,
      indicatorBySection.portfolio.length,
    ].reduce((acc, value) => acc + value, 0),
  };

  return (
    <Stack
      spacing={2}
      sx={{
        mt: { xs: 1.25, md: 2 },
        ...(embedded
          ? // Embedded (chat) takes its natural height so the surrounding
            // conversation is the vertical scroll container.
            { overflow: 'visible' }
          : {
              height: {
                xs: 'calc(100vh - 132px)',
                md: 'calc(100vh - 148px)',
              },
              overflow: 'hidden',
            }),
      }}
    >
      <Paper variant='outlined' sx={{ p: { xs: 1, md: 2 } }}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={{ xs: 1, md: 2 }}
          justifyContent='space-between'
          alignItems={{ xs: 'flex-start', lg: 'flex-start' }}
        >
          <Stack direction='row' spacing={0.5} alignItems='center'>
            {shouldRenderComparatorHeading({ embedded, showHeading }) ? (
              <Typography
                component='h1'
                variant='h5'
                fontWeight={800}
                sx={{
                  fontSize: { xs: 24, md: 30 },
                  lineHeight: { xs: 1.1, md: 1.15 },
                }}
              >
                {getComparatorHeading(selectedAssets)}
              </Typography>
            ) : null}
            <Tooltip
              arrow
              placement='right'
              title='Compare fundos lado a lado usando a mesma estrutura de leitura do resumo. Você pode escolher até 5 ativos, ativar/desativar indicadores por seção e compartilhar a URL com o estado da comparação. O link salva os ativos (ativos=) e os indicadores (indicadores=), então qualquer pessoa abre a mesma visão de comparação.'
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  color: 'hsl(var(--muted-foreground))',
                  cursor: 'help',
                }}
              >
                <CircleHelp size={16} />
              </Box>
            </Tooltip>
          </Stack>

          <Stack spacing={0.8} sx={{ width: { xs: '100%', lg: 'auto' } }}>
            <Stack
              direction='row'
              spacing={{ xs: 0.6, sm: 1 }}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                '& .MuiButton-root': { minWidth: 0 },
              }}
            >
              {embedded ? (
                <Button
                  component={Link}
                  href={fullComparatorHref}
                  variant='outlined'
                  startIcon={<ExternalLink size={14} />}
                  size='small'
                  sx={{
                    color: appColors.actions.accentOutline.text,
                    borderColor: appColors.actions.accentOutline.border,
                    px: { xs: 0.8, sm: 1.25 },
                    py: { xs: 0.35, md: 0.75 },
                    minHeight: { xs: 30, md: 40 },
                    fontSize: { xs: 11, md: 14 },
                    '&:hover': {
                      color: appColors.actions.accentOutline.hoverText,
                      borderColor: appColors.actions.accentOutline.hoverBorder,
                      backgroundColor: appColors.actions.accentOutline.hoverBg,
                    },
                  }}
                >
                  Abrir no Comparador completo
                </Button>
              ) : (
                <>
                  <Button
                    variant='outlined'
                    onClick={() => setIsFundPickerOpen(true)}
                    startIcon={<ListPlus size={14} />}
                    size='small'
                    sx={{
                      color: appColors.actions.accentOutline.text,
                      borderColor: appColors.actions.accentOutline.border,
                      px: { xs: 0.8, sm: 1.25 },
                      py: { xs: 0.35, md: 0.75 },
                      minHeight: { xs: 30, md: 40 },
                      fontSize: { xs: 11, md: 14 },
                      flex: { xs: '1 1 0', sm: '0 0 auto' },
                      '& .MuiButton-startIcon': { mr: { xs: 0.45, md: 1 } },
                      '&:hover': {
                        color: appColors.actions.accentOutline.hoverText,
                        borderColor: appColors.actions.accentOutline.hoverBorder,
                        backgroundColor: appColors.actions.accentOutline.hoverBg,
                      },
                    }}
                  >
                    <Box component='span' sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      Escolher fundos
                    </Box>
                    <Box component='span' sx={{ display: { xs: 'inline', sm: 'none' } }}>
                      Fundos
                    </Box>
                  </Button>
                  <Button
                    variant='outlined'
                    onClick={toggleIndicatorSelectionMode}
                    startIcon={<SlidersHorizontal size={14} />}
                    size='small'
                    sx={{
                      color: appColors.actions.accentOutline.text,
                      borderColor: appColors.actions.accentOutline.border,
                      px: { xs: 0.8, sm: 1.25 },
                      py: { xs: 0.35, md: 0.75 },
                      minHeight: { xs: 30, md: 40 },
                      fontSize: { xs: 11, md: 14 },
                      flex: { xs: '1 1 0', sm: '0 0 auto' },
                      '& .MuiButton-startIcon': { mr: { xs: 0.45, md: 1 } },
                      '&:hover': {
                        color: appColors.actions.accentOutline.hoverText,
                        borderColor: appColors.actions.accentOutline.hoverBorder,
                        backgroundColor: appColors.actions.accentOutline.hoverBg,
                      },
                    }}
                  >
                    {isIndicatorSelectionMode ? 'Finalizar seleção' : 'Escolher indicadores'}
                  </Button>
                </>
              )}
              {!embedded ? (
                <Button
                  variant='contained'
                  onClick={handleShare}
                  startIcon={<Share2 size={14} />}
                  size='small'
                  sx={{
                    px: { xs: 0, sm: 1.25 },
                    py: { xs: 0.35, md: 0.75 },
                    minHeight: { xs: 30, md: 40 },
                    fontSize: { xs: 11, md: 14 },
                    flex: { xs: '0 0 42px', sm: '0 0 auto' },
                    width: { xs: 42, sm: 'auto' },
                    minWidth: { xs: 42, sm: 0 },
                    '& .MuiButton-startIcon': { mr: { xs: 0, md: 1 } },
                  }}
                >
                  <Box component='span' sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    Compartilhar
                  </Box>
                </Button>
              ) : null}
            </Stack>

            {isIndicatorSelectionMode ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent='flex-end'>
                <Button variant='outlined' color='inherit' onClick={() => setDraftIndicatorKeys([])}>
                  Selecionar nenhum
                </Button>
                <Button
                  variant='outlined'
                  onClick={() => setDraftIndicatorKeys(ALL_SELECTABLE_KEYS)}
                  sx={{
                    color: appColors.actions.accentOutline.text,
                    borderColor: appColors.actions.accentOutline.border,
                    '&:hover': {
                      color: appColors.actions.accentOutline.hoverText,
                      borderColor: appColors.actions.accentOutline.hoverBorder,
                      backgroundColor: appColors.actions.accentOutline.hoverBg,
                    },
                  }}
                >
                  Selecionar todos
                </Button>
              </Stack>
            ) : null}
          </Stack>
        </Stack>
      </Paper>

      <Paper
        variant='outlined'
        sx={{
          p: { xs: 1.25, md: 1.5 },
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          {loadingInstruments || loadingFundamentals ? (
            <Typography variant='body2' color='text.secondary'>
              Carregando ativos e fundamentos...
            </Typography>
          ) : selectedAssets.length === 0 ? (
            <Typography variant='body2' color='text.secondary'>
              Selecione pelo menos um ativo para comparar.
            </Typography>
          ) : (
            <Box
              sx={{
                minWidth: {
                  xs: 140 + selectedAssets.length * 130,
                  md: 260 + selectedAssets.length * 170,
                },
                display: 'grid',
                gridTemplateColumns: {
                  xs: `140px repeat(${selectedAssets.length}, minmax(130px, 1fr))`,
                  md: `260px repeat(${selectedAssets.length}, minmax(170px, 1fr))`,
                },
                border: '1px solid hsl(var(--border))',
                borderRadius: 1.25,
                overflow: 'visible',
              }}
            >
              <Box
                sx={{
                  p: 1.1,
                  fontWeight: 700,
                  fontSize: 14,
                  bgcolor: 'hsl(var(--background))',
                  position: { xs: 'static', md: 'sticky' },
                  top: { md: 0 },
                  left: { md: 0 },
                  zIndex: { md: 8 },
                  borderBottom: '1px solid hsl(var(--border))',
                  boxShadow: '0 2px 0 hsl(var(--border))',
                  borderRight: '1px solid hsl(var(--border))',
                }}
              >
                Ativo
              </Box>
              {selectedAssets.map((asset) => (
                <Box
                  key={`header-${asset}`}
                  sx={{
                    p: { xs: 0.55, md: 1.1 },
                    fontWeight: 700,
                    bgcolor: 'hsl(var(--background))',
                    position: { xs: 'static', md: 'sticky' },
                    top: { md: 0 },
                    zIndex: { md: 6 },
                    borderBottom: '1px solid hsl(var(--border))',
                    boxShadow: '0 2px 0 hsl(var(--border))',
                  }}
                >
                  <Box
                    sx={{
                      p: { xs: 0.55, md: 0.8 },
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 1,
                      backgroundColor: 'hsl(var(--card))',
                    }}
                  >
                    <Box
                      component={Link}
                      href={`/ativo/${asset}`}
                      sx={{
                        color: appColors.actions.accentOutline.text,
                        textDecoration: 'none',
                        fontWeight: 700,
                        fontSize: { xs: 12.5, md: 14 },
                        '&:hover': {
                          color: appColors.actions.accentOutline.hoverText,
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {asset}
                    </Box>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{
                        mt: 0.2,
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: { xs: 10, md: 11 },
                      }}
                    >
                      {rowsByAsset[asset]?.name ?? '-'}
                    </Typography>
                    <Typography
                      variant='caption'
                      sx={{
                        display: 'inline-block',
                        mt: { xs: 0.25, md: 0.4 },
                        px: { xs: 0.45, md: 0.6 },
                        borderRadius: 999,
                        border: '1px solid hsl(var(--border))',
                        color: 'hsl(var(--muted-foreground))',
                        fontSize: { xs: 9.5, md: 10.5 },
                      }}
                    >
                      {rowsByAsset[asset]?.segmento ?? 'Sem segmento'}
                    </Typography>
                  </Box>
                </Box>
              ))}

              {activeBasicRows.length > 0 ? (
                <>
                  <SectionHeaderRow
                    title='Dados básicos'
                    count={sectionCounts.basicos}
                    collapsed={collapsedSections.basicos}
                    onToggle={() => toggleSection('basicos')}
                    showCheckbox={isIndicatorSelectionMode}
                    checked={sectionCheckState('basicos').checked}
                    indeterminate={sectionCheckState('basicos').indeterminate}
                    onToggleCheckbox={() => toggleSectionDraft('basicos')}
                  />
                  {!collapsedSections.basicos
                    ? activeBasicRows.map((item, rowIndex) => (
                        <Box key={item.key} sx={{ display: 'contents' }}>
                          <Box
                            sx={{
                              ...stickyLeftCellSx,
                              p: 1,
                              borderTop: '1px solid hsl(var(--border))',
                              bgcolor: zebraRowBg(rowIndex),
                            }}
                          >
                            <IndicatorLabel
                              label={item.label}
                              description={item.description}
                              showCheckbox={isIndicatorSelectionMode}
                              checked={selectedIndicatorKeys.includes(item.key)}
                              onToggle={() => toggleDraftIndicator(item.key)}
                            />
                          </Box>
                          {selectedAssets.map((asset) => (
                            <Box
                              key={`${item.key}-${asset}`}
                              sx={{
                                p: 1,
                                borderTop: '1px solid hsl(var(--border))',
                                bgcolor: zebraRowBg(rowIndex),
                                fontSize: 13,
                                lineHeight: 1.35,
                                color: 'hsl(var(--foreground))',
                              }}
                            >
                              {item.value(rowsByAsset[asset]) ?? '-'}
                            </Box>
                          ))}
                        </Box>
                      ))
                    : null}
                </>
              ) : null}

              {indicatorBySection.fundamentos.length > 0 ? (
                <>
                  <SectionHeaderRow
                    title={SECTION_LABELS.fundamentos}
                    count={sectionCounts.fundamentos}
                    collapsed={collapsedSections.fundamentos}
                    onToggle={() => toggleSection('fundamentos')}
                    showCheckbox={isIndicatorSelectionMode}
                    checked={sectionCheckState('fundamentos').checked}
                    indeterminate={sectionCheckState('fundamentos').indeterminate}
                    onToggleCheckbox={() => toggleSectionDraft('fundamentos')}
                  />
                  {!collapsedSections.fundamentos
                    ? indicatorBySection.fundamentos.map((indicator, rowIndex) =>
                        (() => {
                          const isPrimary = PRIMARY_COMPARATOR_KEYS.has(indicator.key);
                          return (
                            <Box key={indicator.key} sx={{ display: 'contents' }}>
                              <Box
                                sx={{
                                  ...stickyLeftCellSx,
                                  p: 1,
                                  borderTop: '1px solid hsl(var(--border))',
                                  bgcolor: isPrimary ? 'hsl(var(--muted) / 0.35)' : zebraRowBg(rowIndex),
                                }}
                              >
                                <IndicatorLabel
                                  label={indicator.label}
                                  description={indicator.description}
                                  showCheckbox={isIndicatorSelectionMode}
                                  checked={selectedIndicatorKeys.includes(indicator.key)}
                                  onToggle={() => toggleDraftIndicator(indicator.key)}
                                  emphasize={isPrimary}
                                />
                              </Box>
                              {selectedAssets.map((asset) => {
                                const rawValue = indicator.value({
                                  row: rowsByAsset[asset],
                                  risk: riskByAsset[asset] ?? null,
                                  fundamentals: fundamentalsByAsset[asset] ?? null,
                                });
                                const scale = fundamentalsBarScale(indicator.key);
                                const score = normalizeToPercent(rawValue, scale.min, scale.max) ?? 0;
                                const label =
                                  indicator.key === 'pvp' && rawValue !== null && Number.isFinite(rawValue)
                                    ? formatRatio(rawValue)
                                    : formatFundamentalsVisualLabel(indicator, rawValue);
                                const secondaryText = formatFundamentalsSecondary(indicator, rawValue);
                                const accent = fundamentalsIndicatorAccent(appColors, indicator.key, rawValue);
                                return (
                                  <Box
                                    key={`${indicator.key}-${asset}`}
                                    sx={{
                                      p: 1,
                                      borderTop: '1px solid hsl(var(--border))',
                                      bgcolor: isPrimary ? 'hsl(var(--muted) / 0.24)' : zebraRowBg(rowIndex),
                                    }}
                                  >
                                    <MarketIndicatorCell
                                      label={label}
                                      score={score}
                                      accent={accent}
                                      emphasize={isPrimary}
                                      secondaryText={secondaryText}
                                    />
                                  </Box>
                                );
                              })}
                            </Box>
                          );
                        })(),
                      )
                    : null}
                </>
              ) : null}

              {indicatorBySection.mercado.length > 0 ? (
                <>
                  <SectionHeaderRow
                    title={SECTION_LABELS.mercado}
                    count={sectionCounts.mercado}
                    collapsed={collapsedSections.mercado}
                    onToggle={() => toggleSection('mercado')}
                    showCheckbox={isIndicatorSelectionMode}
                    checked={sectionCheckState('mercado').checked}
                    indeterminate={sectionCheckState('mercado').indeterminate}
                    onToggleCheckbox={() => toggleSectionDraft('mercado')}
                  />
                  {!collapsedSections.mercado
                    ? indicatorBySection.mercado.map((indicator, rowIndex) =>
                        (() => {
                          const isPrimary = PRIMARY_COMPARATOR_KEYS.has(indicator.key);
                          return (
                            <Box key={indicator.key} sx={{ display: 'contents' }}>
                              <Box
                                sx={{
                                  ...stickyLeftCellSx,
                                  p: 1,
                                  borderTop: '1px solid hsl(var(--border))',
                                  bgcolor: isPrimary ? 'hsl(var(--muted) / 0.35)' : zebraRowBg(rowIndex),
                                }}
                              >
                                <IndicatorLabel
                                  label={indicator.label}
                                  description={indicator.description}
                                  showCheckbox={isIndicatorSelectionMode}
                                  checked={selectedIndicatorKeys.includes(indicator.key)}
                                  onToggle={() => toggleDraftIndicator(indicator.key)}
                                  emphasize={isPrimary}
                                />
                              </Box>
                              {(() => {
                                const valuesByAsset = Object.fromEntries(
                                  selectedAssets.map((asset) => [
                                    asset,
                                    indicator.value({
                                      row: rowsByAsset[asset],
                                      risk: riskByAsset[asset] ?? null,
                                      fundamentals: fundamentalsByAsset[asset] ?? null,
                                    }),
                                  ]),
                                ) as Record<string, number | null>;
                                const finiteValues = Object.values(valuesByAsset).filter(
                                  (value): value is number => value !== null && Number.isFinite(value),
                                );
                                const hasAnyFinite = finiteValues.length > 0;
                                const scale = marketBarScale(indicator.key);

                                return selectedAssets.map((asset) => {
                                  const rawValue = valuesByAsset[asset];
                                  const label = indicator.format(rawValue);
                                  const score = (() => {
                                    if (rawValue === null || !Number.isFinite(rawValue)) return 0;
                                    if (!hasAnyFinite) return 0;
                                    return normalizeToPercent(rawValue, scale.min, scale.max) ?? 0;
                                  })();
                                  const accent = marketIndicatorAccent(appColors, indicator.key, rawValue);
                                  return (
                                    <Box
                                      key={`${indicator.key}-${asset}`}
                                      sx={{
                                        p: 1,
                                        borderTop: '1px solid hsl(var(--border))',
                                        bgcolor: isPrimary ? 'hsl(var(--muted) / 0.24)' : zebraRowBg(rowIndex),
                                      }}
                                    >
                                      <MarketIndicatorCell
                                        label={label}
                                        score={score}
                                        accent={accent}
                                        emphasize={isPrimary}
                                      />
                                    </Box>
                                  );
                                });
                              })()}
                            </Box>
                          );
                        })(),
                      )
                    : null}
                </>
              ) : null}

              {indicatorBySection.quantitativos.length > 0 ? (
                <>
                  <SectionHeaderRow
                    title={SECTION_LABELS.quantitativos}
                    count={sectionCounts.quantitativos}
                    collapsed={collapsedSections.quantitativos}
                    onToggle={() => toggleSection('quantitativos')}
                    showCheckbox={isIndicatorSelectionMode}
                    checked={sectionCheckState('quantitativos').checked}
                    indeterminate={sectionCheckState('quantitativos').indeterminate}
                    onToggleCheckbox={() => toggleSectionDraft('quantitativos')}
                  />
                  {!collapsedSections.quantitativos
                    ? indicatorBySection.quantitativos.map((indicator, rowIndex) => (
                        <Box key={indicator.key} sx={{ display: 'contents' }}>
                          <Box
                            sx={{
                              ...stickyLeftCellSx,
                              p: 1,
                              borderTop: '1px solid hsl(var(--border))',
                              bgcolor: zebraRowBg(rowIndex),
                            }}
                          >
                            <IndicatorLabel
                              label={indicator.label}
                              description={indicator.description}
                              showCheckbox={isIndicatorSelectionMode}
                              checked={selectedIndicatorKeys.includes(indicator.key)}
                              onToggle={() => toggleDraftIndicator(indicator.key)}
                            />
                          </Box>
                          {selectedAssets.map((asset) => (
                            <Box
                              key={`${indicator.key}-${asset}`}
                              sx={{
                                p: 1,
                                borderTop: '1px solid hsl(var(--border))',
                                bgcolor: zebraRowBg(rowIndex),
                              }}
                            >
                              {(() => {
                                const rawValue = indicator.value({
                                  row: rowsByAsset[asset],
                                  risk: riskByAsset[asset] ?? null,
                                  fundamentals: fundamentalsByAsset[asset] ?? null,
                                });
                                const label = indicator.format(rawValue);
                                return (
                                  <QuantitativeIndicatorCell
                                    indicatorKey={indicator.key}
                                    row={rowsByAsset[asset]}
                                    rawValue={rawValue}
                                    label={label}
                                  />
                                );
                              })()}
                            </Box>
                          ))}
                        </Box>
                      ))
                    : null}
                </>
              ) : null}

              {activeCotacaoCharts.length > 0 ? (
                <>
                  <SectionHeaderRow
                    title='Cotação'
                    count={sectionCounts.cotacao}
                    collapsed={collapsedSections.cotacao}
                    onToggle={() => toggleSection('cotacao')}
                    showCheckbox={isIndicatorSelectionMode}
                    checked={sectionCheckState('cotacao').checked}
                    indeterminate={sectionCheckState('cotacao').indeterminate}
                    onToggleCheckbox={() => toggleSectionDraft('cotacao')}
                  />
                  {!collapsedSections.cotacao ? (
                    <Box
                      sx={{
                        gridColumn: '1 / -1',
                        p: 1.2,
                        borderTop: '1px solid hsl(var(--border))',
                      }}
                    >
                      <Stack spacing={2}>
                        {showQuoteChart ? (
                          <Box>
                            <Box sx={{ mb: 0.9 }}>
                              <IndicatorLabel
                                label='Cotação normalizada (base 100 no início do período)'
                                description={
                                  COTACAO_CHART_ROWS.find((item) => item.key === 'grafico_cotacao_normalizada')
                                    ?.description ?? ''
                                }
                                showCheckbox={isIndicatorSelectionMode}
                                checked={selectedIndicatorKeys.includes('grafico_cotacao_normalizada')}
                                onToggle={() => toggleDraftIndicator('grafico_cotacao_normalizada')}
                              />
                            </Box>
                            <QuoteRangeSelector value={quoteRange} onChange={setQuoteRange} />
                            {loadingPrices ? (
                              <Typography variant='body2' color='text.secondary'>
                                Carregando séries de cotação...
                              </Typography>
                            ) : (
                              <PriceComparisonChart
                                assets={selectedAssets}
                                seriesByAsset={filteredPriceSeriesByAsset}
                              />
                            )}
                          </Box>
                        ) : null}

                        {showTotalReturnChart ? (
                          <Box>
                            <Box sx={{ mb: 0.9 }}>
                              <IndicatorLabel
                                label='Cotação + dividendos acumulados (base 100 no início)'
                                description={
                                  COTACAO_CHART_ROWS.find((item) => item.key === 'grafico_cotacao_dividendos')
                                    ?.description ?? ''
                                }
                                showCheckbox={isIndicatorSelectionMode}
                                checked={selectedIndicatorKeys.includes('grafico_cotacao_dividendos')}
                                onToggle={() => toggleDraftIndicator('grafico_cotacao_dividendos')}
                              />
                            </Box>
                            <QuoteRangeSelector value={quoteRange} onChange={setQuoteRange} />
                            {loadingPrices || loadingDividends ? (
                              <Typography variant='body2' color='text.secondary'>
                                Carregando séries de cotação e dividendos...
                              </Typography>
                            ) : (
                              <TotalReturnComparisonChart
                                assets={selectedAssets}
                                priceSeriesByAsset={filteredPriceSeriesByAsset}
                                dividendSeriesByAsset={filteredDividendSeriesByAsset}
                              />
                            )}
                          </Box>
                        ) : null}

                        {showDividendChart ? (
                          <Box>
                            <Box sx={{ mb: 0.9 }}>
                              <IndicatorLabel
                                label='Dividendos por período (valor por cota)'
                                description={
                                  COTACAO_CHART_ROWS.find((item) => item.key === 'grafico_dividendos_periodo')
                                    ?.description ?? ''
                                }
                                showCheckbox={isIndicatorSelectionMode}
                                checked={selectedIndicatorKeys.includes('grafico_dividendos_periodo')}
                                onToggle={() => toggleDraftIndicator('grafico_dividendos_periodo')}
                              />
                            </Box>
                            <QuoteRangeSelector value={quoteRange} onChange={setQuoteRange} />
                            {loadingDividends ? (
                              <Typography variant='body2' color='text.secondary'>
                                Carregando séries de dividendos...
                              </Typography>
                            ) : (
                              <DividendComparisonChart
                                assets={selectedAssets}
                                seriesByAsset={filteredDividendSeriesByAsset}
                              />
                            )}
                          </Box>
                        ) : null}
                      </Stack>
                    </Box>
                  ) : null}
                </>
              ) : null}

              {indicatorBySection.risco.length > 0 ? (
                <>
                  <SectionHeaderRow
                    title={SECTION_LABELS.risco}
                    count={sectionCounts.risco}
                    collapsed={collapsedSections.risco}
                    onToggle={() => toggleSection('risco')}
                    showCheckbox={isIndicatorSelectionMode}
                    checked={sectionCheckState('risco').checked}
                    indeterminate={sectionCheckState('risco').indeterminate}
                    onToggleCheckbox={() => toggleSectionDraft('risco')}
                  />
                  {!collapsedSections.risco
                    ? indicatorBySection.risco.map((indicator, rowIndex) => (
                        <Box key={indicator.key} sx={{ display: 'contents' }}>
                          <Box
                            sx={{
                              ...stickyLeftCellSx,
                              p: 1,
                              borderTop: '1px solid hsl(var(--border))',
                              bgcolor: zebraRowBg(rowIndex),
                            }}
                          >
                            <IndicatorLabel
                              label={indicator.label}
                              description={indicator.description}
                              showCheckbox={isIndicatorSelectionMode}
                              checked={selectedIndicatorKeys.includes(indicator.key)}
                              onToggle={() => toggleDraftIndicator(indicator.key)}
                            />
                          </Box>
                          {selectedAssets.map((asset) => {
                            const rawValue = indicator.value({
                              row: rowsByAsset[asset],
                              risk: riskByAsset[asset] ?? null,
                              fundamentals: fundamentalsByAsset[asset] ?? null,
                            });
                            const formatted = indicator.format(rawValue);
                            const level = classifyRiskLevelWithColors(appColors, indicator.key, rawValue);
                            const scale = riskBarScale(indicator.key);
                            const barValue = normalizeToPercent(rawValue, scale.min, scale.max);

                            return (
                              <Box
                                key={`${indicator.key}-${asset}`}
                                sx={{
                                  p: 1,
                                  borderTop: '1px solid hsl(var(--border))',
                                  bgcolor: zebraRowBg(rowIndex),
                                  backgroundColor: level.bg,
                                }}
                              >
                                <Stack direction='row' spacing={0.8} alignItems='center' justifyContent='space-between'>
                                  {scale.min < 0 && scale.max > 0 ? (
                                    <VerticalSignedIndicator
                                      value={rawValue}
                                      min={scale.min}
                                      max={scale.max}
                                      label={formatted}
                                    />
                                  ) : (
                                    <VerticalPercentIndicator value={barValue} label={formatted} />
                                  )}
                                  <Chip
                                    size='small'
                                    label={level.label}
                                    sx={{
                                      height: 20,
                                      fontWeight: 700,
                                      fontSize: 11,
                                      color: level.color,
                                      borderColor: `${level.color}66`,
                                      backgroundColor: `${level.color}12`,
                                      maxWidth: '62%',
                                      '& .MuiChip-label': {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        display: 'block',
                                      },
                                    }}
                                    variant='outlined'
                                  />
                                </Stack>
                              </Box>
                            );
                          })}
                        </Box>
                      ))
                    : null}
                </>
              ) : null}

              {showPortfolioTop5 || showPortfolioMix || showPortfolioConcentration ? (
                <>
                  <SectionHeaderRow
                    title={SECTION_LABELS.portfolio}
                    count={sectionCounts.portfolio}
                    collapsed={collapsedSections.portfolio}
                    onToggle={() => toggleSection('portfolio')}
                    showCheckbox={isIndicatorSelectionMode}
                    checked={sectionCheckState('portfolio').checked}
                    indeterminate={sectionCheckState('portfolio').indeterminate}
                    onToggleCheckbox={() => toggleSectionDraft('portfolio')}
                  />
                  {!collapsedSections.portfolio ? (
                    <>
                      {showPortfolioTop5 ? (
                        <Box sx={{ display: 'contents' }}>
                          <Box
                            sx={{
                              p: 1,
                              borderTop: '1px solid hsl(var(--border))',
                            }}
                          >
                            <IndicatorLabel
                              label='Top 5 posições (% PL)'
                              description='Mostra as cinco maiores posições de cada ativo como % do portfólio. Quando a CVM não informa pesos por posição, usamos peso uniforme (1) entre as posições disponíveis para estimar a participação relativa.'
                              showCheckbox={isIndicatorSelectionMode}
                              checked={selectedIndicatorKeys.includes('portfolio_top5')}
                              onToggle={() => toggleDraftIndicator('portfolio_top5')}
                            />
                          </Box>
                          {selectedAssets.map((asset) => {
                            const highlights = portfolioHighlightsByAsset[asset];
                            const topPositions = highlights?.topPositions ?? [];
                            const max = Math.max(
                              1,
                              ...topPositions
                                .map((item) => item.portfolioPercent ?? 0)
                                .filter((value) => Number.isFinite(value)),
                            );
                            return (
                              <Box
                                key={`portfolio-top5-${asset}`}
                                sx={{
                                  p: 1,
                                  borderTop: '1px solid hsl(var(--border))',
                                }}
                              >
                                {loadingPortfolioHighlights ? (
                                  <Typography variant='body2' color='text.secondary'>
                                    Carregando...
                                  </Typography>
                                ) : topPositions.length === 0 ? (
                                  <Typography variant='body2' color='text.secondary'>
                                    Sem posições detalhadas.
                                  </Typography>
                                ) : (
                                  <Stack spacing={0.7}>
                                    {topPositions.map((item) => {
                                      const value = item.portfolioPercent ?? 0;
                                      return (
                                        <Stack key={`${asset}-${item.name}`} spacing={0.35}>
                                          <Stack direction='row' justifyContent='space-between' spacing={0.8}>
                                            <Typography
                                              variant='body2'
                                              sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                              }}
                                            >
                                              {item.name}
                                            </Typography>
                                            <Typography variant='body2' sx={{ fontWeight: 700 }}>
                                              {item.portfolioPercent === null
                                                ? '-'
                                                : `${PERCENT_FORMATTER.format(item.portfolioPercent)}%`}
                                            </Typography>
                                          </Stack>
                                          <LinearProgress
                                            variant='determinate'
                                            value={Math.max(0, Math.min(100, (value / max) * 100))}
                                            sx={{
                                              height: 7,
                                              borderRadius: 999,
                                              backgroundColor: 'hsl(var(--muted))',
                                              '& .MuiLinearProgress-bar': {
                                                backgroundColor: appColors.highlights.progressBar.primary,
                                              },
                                            }}
                                          />
                                        </Stack>
                                      );
                                    })}
                                  </Stack>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      ) : null}

                      {showPortfolioMix ? (
                        <Box sx={{ display: 'contents' }}>
                          <Box
                            sx={{
                              p: 1,
                              borderTop: '1px solid hsl(var(--border))',
                            }}
                          >
                            <IndicatorLabel
                              label='Mix do portfolio'
                              description='Distribuição entre imóveis, fundos, ativos financeiros e participações.'
                              showCheckbox={isIndicatorSelectionMode}
                              checked={selectedIndicatorKeys.includes('portfolio_mix')}
                              onToggle={() => toggleDraftIndicator('portfolio_mix')}
                            />
                          </Box>
                          {selectedAssets.map((asset) => {
                            const mix = portfolioHighlightsByAsset[asset]?.mix;
                            const mixItems = mix
                              ? [
                                  {
                                    label: 'Imóveis',
                                    value: mix.realEstatePercent,
                                    color: appColors.charts.portfolioMix.realEstate,
                                  },
                                  {
                                    label: 'Fundos',
                                    value: mix.fundsPercent,
                                    color: appColors.charts.portfolioMix.funds,
                                  },
                                  {
                                    label: 'Ativos financeiros',
                                    value: mix.financialAssetsPercent,
                                    color: appColors.charts.portfolioMix.financialAssets,
                                  },
                                  {
                                    label: 'Participações',
                                    value: mix.equitiesPercent,
                                    color: appColors.charts.portfolioMix.equities,
                                  },
                                ].sort((a, b) => b.value - a.value)
                              : [];
                            return (
                              <Box
                                key={`portfolio-mix-${asset}`}
                                sx={{
                                  p: 1,
                                  borderTop: '1px solid hsl(var(--border))',
                                }}
                              >
                                {loadingPortfolioHighlights ? (
                                  <Typography variant='body2' color='text.secondary'>
                                    Carregando...
                                  </Typography>
                                ) : mixItems.length === 0 ? (
                                  <Typography variant='body2' color='text.secondary'>
                                    Sem dados de composição.
                                  </Typography>
                                ) : (
                                  <Stack spacing={0.7}>
                                    {mixItems.map((item) => (
                                      <Stack key={`${asset}-${item.label}`} spacing={0.35}>
                                        <Stack direction='row' justifyContent='space-between' spacing={0.8}>
                                          <Stack direction='row' spacing={0.6} alignItems='center'>
                                            <Box
                                              sx={{
                                                width: 9,
                                                height: 9,
                                                borderRadius: 999,
                                                bgcolor: item.color,
                                              }}
                                            />
                                            <Typography variant='body2'>{item.label}</Typography>
                                          </Stack>
                                          <Typography variant='body2' sx={{ fontWeight: 700 }}>
                                            {PERCENT_FORMATTER.format(item.value)}%
                                          </Typography>
                                        </Stack>
                                        <LinearProgress
                                          variant='determinate'
                                          value={Math.max(0, Math.min(100, item.value))}
                                          sx={{
                                            height: 7,
                                            borderRadius: 999,
                                            backgroundColor: 'hsl(var(--muted))',
                                            '& .MuiLinearProgress-bar': {
                                              backgroundColor: item.color,
                                            },
                                          }}
                                        />
                                      </Stack>
                                    ))}
                                  </Stack>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      ) : null}

                      {showPortfolioConcentration ? (
                        <Box sx={{ display: 'contents' }}>
                          <Box
                            sx={{
                              p: 1,
                              borderTop: '1px solid hsl(var(--border))',
                            }}
                          >
                            <IndicatorLabel
                              label='Concentração (Tijolo e Papel)'
                              description='Índice de concentração por perfil: maior posição relativa dentro do perfil. Quando a CVM não informa pesos por posição, usamos peso uniforme (1) entre as posições disponíveis para estimar o cálculo.'
                              showCheckbox={isIndicatorSelectionMode}
                              checked={selectedIndicatorKeys.includes('portfolio_concentracao')}
                              onToggle={() => toggleDraftIndicator('portfolio_concentracao')}
                            />
                          </Box>
                          {selectedAssets.map((asset) => {
                            const concentration = portfolioHighlightsByAsset[asset]?.concentrationByProfile;
                            const tijolo = concentration?.realEstateTop1Percent ?? null;
                            const papel = concentration?.paperTop1Percent ?? null;
                            return (
                              <Box
                                key={`portfolio-conc-${asset}`}
                                sx={{
                                  p: 1,
                                  borderTop: '1px solid hsl(var(--border))',
                                }}
                              >
                                {loadingPortfolioHighlights ? (
                                  <Typography variant='body2' color='text.secondary'>
                                    Carregando...
                                  </Typography>
                                ) : (
                                  <Stack spacing={0.75}>
                                    <Typography variant='body2'>
                                      <strong>Tijolo:</strong>{' '}
                                      {tijolo === null
                                        ? 'Não informado'
                                        : `${PERCENT_FORMATTER.format(tijolo)}% (${concentrationBand(tijolo)})`}
                                    </Typography>
                                    <LinearProgress
                                      variant='determinate'
                                      value={Math.max(0, Math.min(100, tijolo ?? 0))}
                                      sx={{
                                        height: 8,
                                        borderRadius: 999,
                                        backgroundColor: 'hsl(var(--muted))',
                                        '& .MuiLinearProgress-bar': {
                                          backgroundColor: appColors.highlights.progressBar.primary,
                                        },
                                      }}
                                    />
                                    <Typography variant='body2'>
                                      <strong>Papel:</strong>{' '}
                                      {papel === null
                                        ? 'Não informado'
                                        : `${PERCENT_FORMATTER.format(papel)}% (${concentrationBand(papel)})`}
                                    </Typography>
                                    <LinearProgress
                                      variant='determinate'
                                      value={Math.max(0, Math.min(100, papel ?? 0))}
                                      sx={{
                                        height: 8,
                                        borderRadius: 999,
                                        backgroundColor: 'hsl(var(--muted))',
                                        '& .MuiLinearProgress-bar': {
                                          backgroundColor: appColors.highlights.progressBar.warning,
                                        },
                                      }}
                                    />
                                  </Stack>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      ) : null}
                    </>
                  ) : null}
                </>
              ) : null}
            </Box>
          )}
        </Box>

        {missingAssets.length > 0 ? (
          <Typography variant='caption' color='text.secondary' sx={{ mt: 0.9, display: 'block' }}>
            Não encontrados no catálogo: {missingAssets.join(', ')}
          </Typography>
        ) : null}
      </Paper>

      <CompareWithDialog
        open={isFundPickerOpen}
        onClose={() => setIsFundPickerOpen(false)}
        currentInstrument={currentInstrumentForPicker}
        selectedInstruments={selectedAssets}
        onSelectedInstrumentsChange={(next) => updateUrl({ assets: next })}
        maxTotal={MAX_ASSETS}
        showPrimaryAction={false}
      />

      {!embedded ? (
        <Snackbar
          open={shareToastOpen}
          autoHideDuration={1800}
          onClose={() => setShareToastOpen(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert severity='success' variant='filled' onClose={() => setShareToastOpen(false)}>
            URL copiada para a área de transferência.
          </Alert>
        </Snackbar>
      ) : null}
    </Stack>
  );
}
